import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import OrderListPage from './OrderListPage';
import { getConsultInfo, getConsultList, saveConsultInfo, getQuotePrices, getPriceReservationInfo, saveTourPriceInfo, getPriceInfoForReservation, getConsultNotes, saveConsultNote, deleteConsultNote, uploadS3, saveTourSelectedStock, getTourProductResourceList, deleteConsult, addTourProduct, getSupplierCities, updateConsultProductNo, getCommonImagesByCity, downloadCommonImagesToProduct, getProductGallery, saveRepresentativeImage, getOrderStatusListByProductNo, deleteTourProduct, getAgencyList } from '../services/apiService';
import { getEnhancedPriceDataForQuote } from '../services/priceEnhancementService';
import { getUserInfo } from '../utils/tokenManager';
import DateRangePicker from '../components/DateRangePicker';
import TourReservationModal from '../components/TourReservationModal';
import QuotePreviewModal from '../components/QuotePreviewModal';
import QuoteEmailModal from '../components/QuoteEmailModal';
import { generatePdfBlobFromElement } from '../utils/pdfExportUtil';
import ConsultNoteEditModal from '../components/ConsultNoteEditModal';
import { getEstimateExtras } from '../services/estimateExtrasApi';
import { registerItineraryFromJson } from '../services/itineraryRegisterService';
import { getMyBidByCounsel } from '../services/openEstimateApi';

const CHO = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
const HANGUL_BASE = 0xAC00;
const CHOSUNG = 21 * 28;

function getInitials(str) {
  if (!str) return '';
  let res = '';
  for (const ch of String(str)) {
    const code = ch.charCodeAt(0);
    if (code >= 0xAC00 && code <= 0xD7A3) {
      const idx = Math.floor((code - HANGUL_BASE) / CHOSUNG);
      res += CHO[idx] || ch;
    } else {
      res += ch.toUpperCase();
    }
  }
  return res;
}

// 백엔드가 boolean 값을 문자열("false"/"true"), 숫자(0/1) 등 다양한 형태로 되돌려줄 수 있어
// 안전하게 boolean 으로 정규화한다. (예: JSON.stringify(false) → 저장 → 조회 시 "false" 문자열로 복귀)
// `!!"false"` 는 true 이므로 단순 truthy 변환을 쓰면 체크 해제가 저장되지 않는 문제가 발생한다.
function parseZeroFlag(v) {
  if (v == null) return null; // 값 없음 → 판단 불가(null)
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  const s = String(v).trim().toLowerCase();
  if (s === '' ) return null;
  if (s === 'false' || s === '0' || s === 'n' || s === 'no') return false;
  if (s === 'true' || s === '1' || s === 'y' || s === 'yes') return true;
  return null;
}

// ── "일정표에 0원 표시" 체크 상태 로컬 영속화 ────────────────────────────────
// 백엔드 SP(TRP_T_상담정보_01)가 @상담정보 JSON 의 일정표0원표시 필드를 저장하지 않아
// (OPENJSON WITH(...) 목록에 없어 파싱 단계에서 버려짐) 서버에서 값이 돌아오지 않는다.
// 상품 판매가가 0 인 견적은 체크/해제를 구분할 서버 신호가 없으므로 상담번호별로 로컬 저장한다.
const ZERO_FLAG_LS_PREFIX = 'trp:zeroPrice:';
function readZeroFlagLocal(consultNo) {
  try {
    if (consultNo == null) return null;
    const v = window.localStorage.getItem(ZERO_FLAG_LS_PREFIX + String(consultNo));
    if (v == null) return null;
    return v === '1';
  } catch (e) { return null; }
}
function writeZeroFlagLocal(consultNo, val) {
  try {
    if (consultNo == null) return;
    window.localStorage.setItem(ZERO_FLAG_LS_PREFIX + String(consultNo), val ? '1' : '0');
  } catch (e) { /* 저장 실패는 무시 */ }
}

// 인솔자수 컬럼을 체크값 저장소로 재사용 → 0/1 만 우리 플래그로 해석(그 외 값은 우리 것이 아님)
function parseEscortFlag(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  if (n === 1) return true;
  if (n === 0) return false;
  return null;
}

// 체크 상태 복원 우선순위:
// 1) 서버 저장 필드(향후 SP 수정 시) → 2) 인솔자수(0/1) 재사용 저장값(서버 왕복, 전 사용자 공유) →
// 3) 상품 판매가가 양수면 '해제'로 확정 → 4) 상품가 0(모호)이면 로컬 저장값 → 5) 상품 전부 0 추론
function resolveZeroFlag({ fieldVal, escortVal, allZeroTotalSale, consultNo }) {
  const f = parseZeroFlag(fieldVal);
  if (f != null) return f;
  const e = parseEscortFlag(escortVal);
  if (e != null) return e;
  if (allZeroTotalSale === false) return false; // 일정표에 실판매가가 찍혀 있으면 = 해제 상태
  const ls = readZeroFlagLocal(consultNo);
  if (ls != null) return ls;
  if (typeof allZeroTotalSale === 'boolean') return allZeroTotalSale;
  return null;
}

const MAX_AUTO_REPRESENTATIVE_IMAGES = 10;

/** 도시 공용이미지에서 랜덤 N장을 상품 대표이미지로 자동 등록 (best-effort) */
async function autoRegisterRepresentativeImagesFromCity({ companyNo, userNo, productNo, cityNo, logLabel = '[상품 생성]' }) {
  try {
    const commonImages = await getCommonImagesByCity({ cityNo: String(cityNo) });
    if (!Array.isArray(commonImages) || commonImages.length === 0) return;

    const shuffled = [...commonImages].sort(() => Math.random() - 0.5);
    const selectedImages = shuffled.slice(0, Math.min(MAX_AUTO_REPRESENTATIVE_IMAGES, shuffled.length));
    const selectedImageNos = selectedImages.map((img) => img.이미지번호 || img.ImageNo).filter(Boolean);
    if (!selectedImageNos.length) return;

    await downloadCommonImagesToProduct({
      companyNo,
      userNo,
      productNo,
      imageNos: selectedImageNos,
    });

    const gallery = await getProductGallery({ companyNo, productNo });
    if (!Array.isArray(gallery) || gallery.length === 0) return;

    const imagesToRegister = gallery.slice(0, Math.min(MAX_AUTO_REPRESENTATIVE_IMAGES, gallery.length));
    for (const img of imagesToRegister) {
      const imgNo = img.이미지번호 || img.ImageNo;
      if (imgNo) {
        await saveRepresentativeImage({ imageNo: imgNo, productNo, userNo });
      }
    }
  } catch (imgErr) {
    console.warn(`${logLabel} 대표이미지 자동 등록 실패 (무시됨):`, imgErr);
  }
}

// 견적 상세: 상단에 상담정보를 노출하고, 하단에는 기존 주문내역 테이블 UI를 재사용합니다.
export default function QuoteDetailPage() {
  const { productCode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const navState = (location && location.state) || {};
  const { consultNo: navConsultNo, productNo: navProductNo, productName: navProductName, fromNewQuote } = navState;

  const routeProductNo = useMemo(() => {
    const n = Number(productCode);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [productCode]);

  const [consultNo, setConsultNo] = useState(navConsultNo || null);
  const [consultInfo, setConsultInfo] = useState(null);
  // 상담상태 코드 (420003 상담요청, 420001 진행중, 420002 예약전환, 420999 상담취소)
  const [consultStatusCode, setConsultStatusCode] = useState(null);
  // 공개 견적 응찰 상태 (Supabase estimate_bids.status: won/lost/submitted/bidding)
  // 판매사가 낙찰하면 won/lost 로 바뀐다. 상담 SP 밖의 값이라 별도 조회한다.
  const [bidStatus, setBidStatus] = useState(null);
  // 이 상담을 만든 공개 견적 요청의 대리점 표시 라벨 (참여건이 아니면 null)
  const [bidAgencyLabel, setBidAgencyLabel] = useState(null);
  const [isConsultStatusOpen, setIsConsultStatusOpen] = useState(false);
  const [reservationStatusConfirmOpen, setReservationStatusConfirmOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // 파일 업로드 상태
  const [uploading, setUploading] = useState(false);
  const manualAttachInputRef = useRef(null);
  // 상담기록 상태
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);

  // 예약 모달
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reservationListOpen, setReservationListOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailPdfUrl, setEmailPdfUrl] = useState('');
  const [emailGenerating, setEmailGenerating] = useState(false);

  const [costItemModalOpen, setCostItemModalOpen] = useState(false);
  const [costDraftList, setCostDraftList] = useState([]);

  // 예약전환 상태 여부 (편집 잠금용)
  const isLockedByReservation = String(consultStatusCode || '') === '420002';

  useEffect(() => {
    if (!costItemModalOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setCostItemModalOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [costItemModalOpen]);

  // Editable form state
  const [form, setForm] = useState({
    요청자명: '',
    요청자휴대폰: '',
    요청자이메일: '',
    판매업체: '',
    판매업체번호: '',
    수배처: null,
    수배처번호: null,
    시작일: '',
    종료일: '',
    견적명: '',
    견적내용: '',
    여행지도시: '',
    성인수: 0,
    소아수: 0,
    유아수: 0,
    성인판매가: 0,
    소아판매가: 0,
    유아판매가: 0,
    성인원가: 0,
    소아원가: 0,
    유아원가: 0,
    견적첨부파일리스트: [],
    세부원가목록: [],
  });

  // 판매거래처 picker
  const [agencyList, setAgencyList] = useState([]);
  const [agencyLoading, setAgencyLoading] = useState(false);
  const [agencyError, setAgencyError] = useState('');
  const [agencySearch, setAgencySearch] = useState('');
  const [showAgencyPicker, setShowAgencyPicker] = useState(false);
  const agencyDropdownRef = useRef(null);

  // 상품 가격/재고 동기화 옵션
  const [syncToProduct, setSyncToProduct] = useState(true);
  const [zeroPriceToProduct, setZeroPriceToProduct] = useState(fromNewQuote ? true : !navConsultNo); // 신규 견적 생성 후 최초 진입 시 기본 체크
  const zeroPriceTouchedRef = useRef(false);
  const [priceNo, setPriceNo] = useState(null); // 조회된 가격번호

  // 상품 생성 관련 상태
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [currentProductNo, setCurrentProductNo] = useState(navProductNo || null);
  const [autoProductCreated, setAutoProductCreated] = useState(false);

  const [cityPickOpen, setCityPickOpen] = useState(false);
  const [cityPickLoading, setCityPickLoading] = useState(false);
  const [cityPickError, setCityPickError] = useState('');
  const [cityPickQuery, setCityPickQuery] = useState('');
  const [cityPickOptions, setCityPickOptions] = useState([]);
  const [cityPickSelectedCode, setCityPickSelectedCode] = useState('');
  const cityPickPromiseRef = useRef(null);

  const effectiveProductNo = useMemo(() => {
    if (currentProductNo != null && Number(currentProductNo) > 0) return Number(currentProductNo);
    if (navProductNo != null && Number(navProductNo) > 0) return Number(navProductNo);
    if (routeProductNo != null && Number(routeProductNo) > 0) return Number(routeProductNo);
    const tc = consultInfo?.팀컬러;
    const first = Array.isArray(tc) ? tc[0] : tc;
    if (first == null) return null;
    const digits = String(first).replace(/[^0-9]/g, '');
    const n = Number(digits);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [currentProductNo, navProductNo, routeProductNo, consultInfo]);

  const filteredCityPickOptions = useMemo(() => {
    const q = (cityPickQuery || '').trim();
    if (!q) return cityPickOptions;
    const qUpper = q.toUpperCase();
    const qCho = getInitials(q);

    const rank = (name) => {
      const upper = String(name || '').toUpperCase();
      const nameCho = getInitials(name || '');
      if (upper === qUpper) return 0;
      if (upper.startsWith(qUpper)) return 10;
      if (upper.includes(qUpper)) return 20;
      if (nameCho === qCho) return 30;
      if (nameCho.startsWith(qCho)) return 40;
      if (nameCho.includes(qCho)) return 50;
      return 999;
    };

    return (cityPickOptions || [])
      .filter((opt) => {
        const name = String(opt?.코드명 || '');
        const upper = name.toUpperCase();
        const nameCho = getInitials(name);
        return upper.includes(qUpper) || nameCho.includes(qCho);
      })
      .sort((a, b) => {
        const ra = rank(a?.코드명);
        const rb = rank(b?.코드명);
        if (ra !== rb) return ra - rb;
        return String(a?.코드명 || '').localeCompare(String(b?.코드명 || ''));
      });
  }, [cityPickOptions, cityPickQuery]);

  const openCityPicker = async ({ companyNo, initialCityCode, initialCityName } = {}) => {
    const { companyNo: tokenCompanyNo } = getUserInfo() || {};
    const cn = companyNo || tokenCompanyNo;
    if (!cn) throw new Error('사용자 정보를 확인할 수 없습니다. 다시 로그인 후 시도해주세요.');

    // hanmaeum 에서 사이드카(estimate_extras)로 전달된 여행지 도시.
    // 명시적 코드가 없을 때 이 이름을 도시 목록과 매칭해 자동 선택한다.
    const autoName = String(
      initialCityName || form.여행지도시 || consultInfo?.extras?.destination || '',
    ).trim();

    setCityPickError('');
    setCityPickQuery('');
    setCityPickSelectedCode(String(initialCityCode || ''));
    setCityPickOpen(true);

    // 도시 목록 확보(캐시 우선, 없으면 조회)
    let arr = Array.isArray(cityPickOptions) ? cityPickOptions : [];
    if (arr.length === 0) {
      try {
        setCityPickLoading(true);
        const cities = await getSupplierCities({ companyNo: cn });
        arr = Array.isArray(cities) ? cities : [];
        setCityPickOptions(arr);
        if (arr.length === 0) {
          setCityPickError('도시 목록이 비어있습니다. 관리자에게 문의하세요.');
        }
      } catch (e) {
        console.warn('도시 목록 조회 실패:', e);
        setCityPickError('도시 목록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.');
        setCityPickOptions([]);
        arr = [];
      } finally {
        setCityPickLoading(false);
      }
    }

    // 명시 코드가 없고 여행지 도시 이름이 있으면 이름→코드 자동 선택 + 검색어 프리필.
    // 이름(초성 포함) 매칭이며, 양방향 포함(코드명⊆도시명 / 도시명⊆코드명)도 허용한다.
    if (!initialCityCode && autoName && arr.length) {
      const nUpper = autoName.toUpperCase();
      const nCho = getInitials(autoName);
      const rankOf = (nm) => {
        const u = String(nm || '').toUpperCase();
        const c = getInitials(nm || '');
        if (!u) return 999;
        if (u === nUpper) return 0;
        if (u.startsWith(nUpper)) return 10;
        if (u.includes(nUpper)) return 20;
        if (u.length >= 2 && nUpper.includes(u)) return 25; // 코드명이 여행지 문자열의 일부
        if (c && c === nCho) return 30;
        if (c && c.startsWith(nCho)) return 40;
        if (c && c.includes(nCho)) return 50;
        return 999;
      };
      let best = null;
      let bestRank = 999;
      for (const opt of arr) {
        const r = rankOf(opt?.코드명 || opt?.도시명);
        if (r < bestRank) {
          bestRank = r;
          best = opt;
        }
      }
      if (best && bestRank < 999) {
        const code = String(best?.코드 || best?.도시번호 || '');
        if (code) {
          setCityPickSelectedCode(code);
          // 원본 여행지 텍스트가 아니라 매칭된 도시명으로 프리필해야
          // 목록 필터에서 그 도시가 항상 보인다.
          setCityPickQuery(String(best?.코드명 || best?.도시명 || autoName));
        }
      }
    }

    return await new Promise((resolve) => {
      cityPickPromiseRef.current = { resolve };
    });
  };

  const closeCityPicker = (result) => {
    try {
      const resolver = cityPickPromiseRef.current;
      cityPickPromiseRef.current = null;
      setCityPickOpen(false);
      setCityPickError('');
      setCityPickQuery('');
      if (resolver && typeof resolver.resolve === 'function') {
        resolver.resolve(result);
      }
    } catch {
      setCityPickOpen(false);
    }
  };

  // Fallback: derive consultNo via 상담리스트 if not passed from navigation
  useEffect(() => {
    let cancelled = false;
    const ensureConsultNo = async () => {
      try {
        if (navConsultNo) { setConsultNo(navConsultNo); return; }
        const pnCandidate = navProductNo || routeProductNo;
        if (!pnCandidate) { return; }
        const list = await getConsultList();
        // Map 팀컬러(숫자) -> 상담번호, then find by productNo
        const pn = Number(pnCandidate);
        const found = (Array.isArray(list) ? list : []).find(r => {
          const teamColor = r?.팀컬러 != null ? Number(String(r.팀컬러).replace(/[^0-9]/g, '')) : null;
          return teamColor === pn;
        });
        if (!cancelled && found?.상담번호 != null) {
          setConsultNo(Number(found.상담번호));
        }
      } catch (e) {
        if (!cancelled) console.error('상담번호 도출 실패:', e);
      }
    };

    ensureConsultNo();
    return () => { cancelled = true; };
  }, [navConsultNo, navProductNo, routeProductNo]);

  // Build a lightweight quote HTML for PDF generation (without opening preview modal)
  const buildAutoQuoteElement = () => {
    const wrapper = document.createElement('div');
    wrapper.style.width = '794px';
    wrapper.style.maxWidth = '794px';
    wrapper.style.background = '#ffffff';
    wrapper.style.color = '#111827';
    wrapper.style.fontFamily = "Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Malgun Gothic', Arial, sans-serif";
    wrapper.style.padding = '20px';

    const fmtLocal = (v) => {
      const n = Number(v || 0);
      if (!Number.isFinite(n)) return '0';
      return n.toLocaleString('ko-KR');
    };
    const adultCount = Number(form?.성인수 || 0);
    const childCount = Number(form?.소아수 || 0);
    const infantCount = Number(form?.유아수 || 0);
    const adultPrice = Number(form?.성인판매가 || 0);
    const childPrice = Number(form?.소아판매가 || 0);
    const infantPrice = Number(form?.유아판매가 || 0);
    const adultTotal = adultPrice * adultCount;
    const childTotal = childPrice * childCount;
    const infantTotal = infantPrice * infantCount;
    const totalPeople = adultCount + childCount + infantCount;
    const totalSale = adultTotal + childTotal + infantTotal;
    const periodStr = form?.시작일 && form?.종료일 ? `${form.시작일} ~ ${form.종료일}` : (form?.시작일 || form?.종료일 || '-')
    const today = new Date().toLocaleDateString('ko-KR');

    wrapper.innerHTML = `
      <div style="border-bottom:2px solid #2563eb; padding-bottom:12px; margin-bottom:16px;">
        <div style="font-size:20px; font-weight:700;">${form?.견적명 || navProductName || '여행 상품 견적서'}</div>
        <div style="font-size:12px; color:#6b7280; margin-top:4px;">생성일: ${today}</div>
      </div>
      <div style="display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:16px;">
        <div style="border:1px solid #e5e7eb; border-radius:8px; padding:12px;">
          <div style="font-size:13px; font-weight:600; color:#374151; margin-bottom:8px;">고객 정보</div>
          <div style="font-size:13px; color:#111827; line-height:1.6;">
            <div><span style="color:#6b7280; margin-right:8px;">요청자명</span>${form?.요청자명 || '-'}</div>
            <div><span style="color:#6b7280; margin-right:8px;">휴대폰</span>${form?.요청자휴대폰 || '-'}</div>
            <div><span style="color:#6b7280; margin-right:8px;">이메일</span>${form?.요청자이메일 || '-'}</div>
          </div>
        </div>
        <div style="border:1px solid #e5e7eb; border-radius:8px; padding:12px;">
          <div style="font-size:13px; font-weight:600; color:#374151; margin-bottom:8px;">여행 일정 & 인원</div>
          <div style="font-size:13px; color:#111827; line-height:1.6;">
            <div><span style="color:#6b7280; margin-right:8px;">여행기간</span>${periodStr}</div>
            <div><span style="color:#6b7280; margin-right:8px;">성인</span>${adultCount}명</div>
            ${childCount > 0 ? `<div><span style="color:#6b7280; margin-right:8px;">소아</span>${childCount}명</div>` : ''}
            ${infantCount > 0 ? `<div><span style="color:#6b7280; margin-right:8px;">유아</span>${infantCount}명</div>` : ''}
            <div style="border-top:1px solid #f3f4f6; margin-top:6px; padding-top:6px;"><span style="color:#6b7280; margin-right:8px;">총 인원</span><span style="font-weight:700; color:#111827;">${totalPeople}명</span></div>
          </div>
        </div>
      </div>
      <div style="border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin-bottom:16px;">
        <div style="font-size:13px; font-weight:600; color:#374151; margin-bottom:8px;">견적 내용</div>
        <div style="white-space:pre-wrap; font-size:13px; color:#111827;">${(form?.견적내용 || '').toString().replace(/</g,'&lt;')}</div>
      </div>
      <div style="border:1px solid #e5e7eb; border-radius:8px; padding:12px;">
        <div style="font-size:13px; font-weight:600; color:#374151; margin-bottom:8px;">가격 요약 (판매가 기준)</div>
        <table style="width:100%; border-collapse:collapse; font-size:13px;">
          <thead>
            <tr style="background:#f9fafb; color:#374151;">
              <th style="text-align:left; padding:8px; border:1px solid #e5e7eb;">구분</th>
              <th style="text-align:right; padding:8px; border:1px solid #e5e7eb;">1인 가격</th>
              <th style="text-align:right; padding:8px; border:1px solid #e5e7eb;">인원</th>
              <th style="text-align:right; padding:8px; border:1px solid #e5e7eb;">합계</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="padding:8px; border:1px solid #e5e7eb;">성인</td>
              <td style="padding:8px; border:1px solid #e5e7eb; text-align:right;">${fmtLocal(adultPrice)}원</td>
              <td style="padding:8px; border:1px solid #e5e7eb; text-align:right;">${fmtLocal(adultCount)}</td>
              <td style="padding:8px; border:1px solid #e5e7eb; text-align:right;">${fmtLocal(adultTotal)}원</td>
            </tr>
            ${childCount > 0 ? `
            <tr>
              <td style="padding:8px; border:1px solid #e5e7eb;">소아</td>
              <td style="padding:8px; border:1px solid #e5e7eb; text-align:right;">${fmtLocal(childPrice)}원</td>
              <td style="padding:8px; border:1px solid #e5e7eb; text-align:right;">${fmtLocal(childCount)}</td>
              <td style="padding:8px; border:1px solid #e5e7eb; text-align:right;">${fmtLocal(childTotal)}원</td>
            </tr>` : ''}
            ${infantCount > 0 ? `
            <tr>
              <td style="padding:8px; border:1px solid #e5e7eb;">유아</td>
              <td style="padding:8px; border:1px solid #e5e7eb; text-align:right;">${fmtLocal(infantPrice)}원</td>
              <td style="padding:8px; border:1px solid #e5e7eb; text-align:right;">${fmtLocal(infantCount)}</td>
              <td style="padding:8px; border:1px solid #e5e7eb; text-align:right;">${fmtLocal(infantTotal)}원</td>
            </tr>` : ''}
          </tbody>
          <tfoot>
            <tr>
              <td style="padding:8px; border:1px solid #e5e7eb; text-align:right; font-weight:600;" colspan="3">총 판매가</td>
              <td style="padding:8px; border:1px solid #e5e7eb; text-align:right; font-weight:600;">${fmtLocal(totalSale)}원</td>
            </tr>
          </tfoot>
        </table>
      </div>
    `;
    return wrapper;
  };

  const onAutoGenerateAndOpenEmail = async () => {
    try {
      setEmailGenerating(true);
      const element = buildAutoQuoteElement();
      const blob = await generatePdfBlobFromElement(element);
      const safeNameBase = (form?.견적명 || navProductName || '견적').replace(/["<>:|?*\\/]/g, ' ');
      const stampedName = makeTimestampedName(`${safeNameBase}.pdf`);
      const { url } = await uploadS3({ file: blob, fileName: stampedName, filePath: ATTACH_PATH });
      const pathOnly = `${ATTACH_PATH}${stampedName}`;
      const newItem = { 첨부파일번호: 0, 파일명: stampedName, 첨부파일경로: pathOnly, url, 자동생성: true };
      const nextForm = {
        ...form,
        견적첨부파일리스트: Array.isArray(form.견적첨부파일리스트)
          ? [...form.견적첨부파일리스트, newItem]
          : [newItem]
      };
      setForm(nextForm);
      
      // 업로드 완료 후 곧바로 상담정보 저장(첨부파일 반영)
      console.log('자동 생성 견적서 저장 시도:', { consultNo, effectiveProductNo, consultStatusCode });
      try {
        await saveConsultInfo({
          consultNo,
          productNo: effectiveProductNo,
          form: nextForm,
          consultStatusCode,
          consultTypeCode: '419002',
          zeroPriceToProduct,
        });
        console.log('자동 생성 견적서 저장 성공');
      } catch (saveErr) {
        console.error('자동 생성 견적서 저장 실패:', saveErr);
        alert('자동 생성 견적서 저장에 실패했습니다: ' + (saveErr?.message || saveErr));
      }
      
      setEmailPdfUrl(url);
      setEmailOpen(true);
    } catch (e) {
      console.error('견적서 자동 생성/첨부 실패:', e);
      alert(e?.message || '견적서 PDF 생성/첨부 중 오류가 발생했습니다.');
    } finally {
      setEmailGenerating(false);
    }
  };

  // Auto-fetch prices when productNo and 시작일 are available/changed
  useEffect(() => {
    if (!effectiveProductNo) return;
    if (!form.시작일) return;
    // 상담(견적) 판매가가 이미 존재하는 경우, 상품 판매가 조회로 폼을 덮어쓰지 않도록 방지
    const consultAdult = Number(consultInfo?.성인판매가 || 0);
    const consultChild = Number(consultInfo?.소아판매가 || 0);
    const consultInfant = Number(consultInfo?.유아판매가 || 0);
    const formAdult = Number(form?.성인판매가 || 0);
    const formChild = Number(form?.소아판매가 || 0);
    const formInfant = Number(form?.유아판매가 || 0);

    const hasConsultSale = consultAdult > 0 || consultChild > 0 || consultInfant > 0;
    const hasFormSale = formAdult > 0 || formChild > 0 || formInfant > 0;
    if (hasConsultSale || hasFormSale) return;
    onFetchPrices();
  }, [effectiveProductNo, form.시작일, consultInfo, form.성인판매가, form.소아판매가, form.유아판매가]);

    // Auto-init: restore checkbox from saved consult setting, or infer from product prices.
    // This runs even when 상담/폼 판매가가 존재하여 onFetchPrices()가 스킵되는 경우에도 체크박스를 복원하기 위함.
    useEffect(() => {
      let cancelled = false;
      const initZeroPriceCheck = async () => {
        if (fromNewQuote) return;
        if (!effectiveProductNo) return;
        if (!form.시작일) return;
        const savedZero = parseZeroFlag(consultInfo?.일정표0원표시);
        if (savedZero != null) {
          if (!cancelled) setZeroPriceToProduct(savedZero);
          return;
        }
        const escortZero = parseEscortFlag(consultInfo?.인솔자수);
        if (escortZero != null) {
          if (!cancelled) setZeroPriceToProduct(escortZero);
          return;
        }
        try {
          const date = form.시작일;
          const { allZeroTotalSale } = await getQuotePrices({ productNo: effectiveProductNo, startDate: date });
          const resolved = resolveZeroFlag({ fieldVal: null, escortVal: null, allZeroTotalSale, consultNo });
          if (!cancelled && resolved != null) {
            setZeroPriceToProduct(resolved);
          }
        } catch (e) {
          // ignore (checkbox init is best-effort)
        }
      };
      initZeroPriceCheck();
      return () => { cancelled = true; };
    }, [effectiveProductNo, form.시작일, fromNewQuote, consultInfo?.일정표0원표시, consultInfo?.인솔자수]);

  // Ensure URL has productCode so that OrderListPage header can display product number
  useEffect(() => {
    if (!effectiveProductNo) return;
    if (routeProductNo != null && Number(routeProductNo) > 0) return;
    navigate(`/quote-detail/${effectiveProductNo}`, {
      state: {
        ...(navState || {}),
        consultNo,
        productNo: effectiveProductNo,
        productName: navProductName,
      },
      replace: true,
    });
  }, [effectiveProductNo, routeProductNo, consultNo, navProductName, navState, navigate]);

  // Load consult info when consultNo is available
  useEffect(() => {
    let cancelled = false;
    const loadInfo = async () => {
      if (!consultNo) { setLoading(false); return; }
      try {
        setLoading(true);
        setError(null);
        const info = await getConsultInfo({ consultNo });
        if (!cancelled) setConsultInfo(info || null);
      } catch (e) {
        if (!cancelled) {
          console.error('상담정보 조회 실패:', e);
          setError(e?.message || '상담정보를 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadInfo();
    return () => { cancelled = true; };
  }, [consultNo]);

  // Load 공개 견적 응찰 상태(Supabase) when consultNo is available.
  // 판매사가 낙찰(award_estimate_bid)하면 estimate_bids.status 가 won/lost 가 되므로,
  // 이 상담이 공개 견적 참여로 생성된 건이면 낙찰/유찰 배지를 띄운다. 아니면 null.
  useEffect(() => {
    let cancelled = false;
    const loadBid = async () => {
      if (!consultNo) { setBidStatus(null); setBidAgencyLabel(null); return; }
      try {
        const bid = await getMyBidByCounsel(consultNo);
        if (!cancelled) {
          setBidStatus(bid?.status || null);
          setBidAgencyLabel(bid?.agency_label || null);
        }
      } catch (e) {
        if (!cancelled) console.warn('응찰 상태 조회 실패 (무시):', e);
      }
    };
    loadBid();
    return () => { cancelled = true; };
  }, [consultNo]);

  // Load consult notes list when consultNo is available
  useEffect(() => {
    let cancelled = false;
    const loadNotes = async () => {
      if (!consultNo) { setNotes([]); return; }
      try {
        setNotesLoading(true);
        setNotesError(null);
        const list = await getConsultNotes({ consultNo });
        if (!cancelled) setNotes(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) {
          console.error('상담기록 조회 실패:', e);
          setNotesError(e?.message || '상담기록을 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) setNotesLoading(false);
      }
    };
    loadNotes();
    return () => { cancelled = true; };
  }, [consultNo]);

  // Fallback: if consultInfo has no 팀컬러(상품번호 매칭키), derive it from 상담리스트
  useEffect(() => {
    let cancelled = false;
    const ensureProductNoFromConsultList = async () => {
      try {
        if (!consultNo) return;
        if (currentProductNo != null && Number(currentProductNo) > 0) return;
        if (navProductNo != null && Number(navProductNo) > 0) return;
        if (routeProductNo != null && Number(routeProductNo) > 0) return;

        const teamColorFromDetail = consultInfo?.팀컬러;
        const first = Array.isArray(teamColorFromDetail) ? teamColorFromDetail[0] : teamColorFromDetail;
        if (first != null && String(first).replace(/[^0-9]/g, '')) return;

        const list = await getConsultList();
        const row = (Array.isArray(list) ? list : []).find(r => Number(r?.상담번호) === Number(consultNo));
        const tc = row?.팀컬러;
        const digits = tc != null ? String(tc).replace(/[^0-9]/g, '') : '';
        const n = Number(digits);
        if (!cancelled && Number.isFinite(n) && n > 0) {
          setCurrentProductNo(n);
        }
      } catch (e) {
        if (!cancelled) console.warn('상품번호(팀컬러) 복원 실패:', e);
      }
    };

    ensureProductNoFromConsultList();
    return () => { cancelled = true; };
  }, [consultNo, consultInfo, currentProductNo, navProductNo, routeProductNo]);

  // 상담정보는 있으나 연결된 상품이 전혀 없을 때: 진입 시 한 번,
  // "도시 선택 → 상품 생성 → 상품번호만 연결/저장"을 onSave 한 흐름으로 처리한다.
  // (가격/재고 동기화는 하지 않음 — 사용자가 [저장] 시 체크박스로 선택)
  // (도시 선택 모달은 onSave 내부에서 열리며, hanmaeum 여행지로 자동 선택된다.)
  // → 예전처럼 여기서 상품만 따로 만들지 않으므로, 저장 때 도시를 다시 묻는 중복이 사라진다.
  useEffect(() => {
    let cancelled = false;

    const autoCreateAndSave = async () => {
      try {
        if (cancelled) return;
        if (autoProductCreated) return;
        if (!consultNo) return;

        // 이미 유효한 상품번호가 있으면 스킵
        if (effectiveProductNo != null && Number(effectiveProductNo) > 0) return;

        // onSave 는 form 값을 그대로 저장하므로, form 이 상담정보로 채워진 뒤에만 진행.
        // (아직이면 다음 렌더에서 재시도된다 — deps 에 form/consultInfo 포함)
        const name = (form?.견적명 || '').trim();
        if (!name || !form?.시작일 || !form?.종료일) return;

        // 한 번만 실행되도록 표시 후, 도시선택→상품생성→상품번호 연결만 수행.
        // 사용자가 도시 모달을 취소하면 onSave 가 중단되며, 이후 [저장] 버튼으로 재시도 가능.
        setAutoProductCreated(true);
        await onSave({ syncToProduct: false, quiet: true });
      } catch (e) {
        console.warn('[진입 시 자동 상품생성+저장] 실패 (무시):', e);
      }
    };

    autoCreateAndSave();
    return () => { cancelled = true; };
  }, [consultNo, consultInfo, effectiveProductNo, autoProductCreated, form, navigate, navState]);

  // 상담기록 저장 (신규/수정) - 모달 저장
  const onSaveNote = async (contentArg) => {
    if (!consultNo) return;
    const content = String((contentArg ?? noteContent) || '').trim();
    if (!content) return;
    try {
      setNoteSaving(true);
      await saveConsultNote({ consultNo, recordNo: editingId || 0, content });
      const list = await getConsultNotes({ consultNo });
      setNotes(Array.isArray(list) ? list : []);
      setEditingId(null);
      setNoteContent('');
      setNoteModalOpen(false);
    } catch (e) {
      console.error('상담기록 저장 실패:', e);
      alert(e?.message || '상담기록 저장에 실패했습니다.');
    } finally {
      setNoteSaving(false);
    }
  };

  // (미사용) 자동 저장 로직은 모달 저장으로 대체
  const onNoteBlur = async () => {};

  const onEditNote = (row) => {
    setEditingId(row?.상담기록번호 ?? null);
    setNoteContent(row?.내용 ?? '');
    setNoteModalOpen(true);
  };

  const onCancelEdit = () => {
    setEditingId(null);
    setNoteContent('');
    setNoteModalOpen(false);
  };

  const onDeleteNote = async (row) => {
    const recordNo = row?.상담기록번호;
    if (!recordNo) return;
    if (!window.confirm('해당 상담기록을 삭제하시겠습니까?')) return;
    try {
      setNotesLoading(true);
      await deleteConsultNote({ recordNo });
      // if currently editing this record, reset editor
      if (editingId && Number(editingId) === Number(recordNo)) {
        setEditingId(null);
        setNoteContent('');
      }
      const list = await getConsultNotes({ consultNo });
      setNotes(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('상담기록 삭제 실패:', e);
      alert(e?.message || '상담기록 삭제에 실패했습니다.');
    } finally {
      setNotesLoading(false);
    }
  };

  // Sync form with loaded consultInfo
  useEffect(() => {
    const i = consultInfo || {};
    setForm({
      요청자명: i.요청자명 ?? '',
      요청자휴대폰: i.요청자휴대폰 ?? '',
      요청자이메일: i.요청자이메일 ?? '',
      판매업체: i.판매업체 ?? i.판매업체명 ?? i.판매처 ?? '',
      판매업체번호: i.판매업체번호 != null ? String(i.판매업체번호) : '',
      수배처: i.수배처 ?? null,
      수배처번호: i.수배처번호 ?? null,
      시작일: i.시작일 ?? '',
      종료일: i.종료일 ?? '',
      견적명: i.견적명 ?? '',
      견적내용: i.견적내용 ?? '',
      여행지도시: i.여행지도시 ?? i.extras?.destination ?? i.extras?.city_name ?? '',
      성인수: i.성인수 ?? 0,
      소아수: i.소아수 ?? 0,
      유아수: i.유아수 ?? 0,
      성인판매가: i.성인판매가 ?? 0,
      소아판매가: i.소아판매가 ?? 0,
      유아판매가: i.유아판매가 ?? 0,
      성인원가: i.성인원가 ?? 0,
      소아원가: i.소아원가 ?? 0,
      유아원가: i.유아원가 ?? 0,
      견적첨부파일리스트: Array.isArray(i.견적첨부파일리스트) ? i.견적첨부파일리스트 : [],
      세부원가목록: Array.isArray(i.세부원가) ? i.세부원가.map(row => ({
        원가항목: row.원가항목 ?? '',
        성인: Number(row.성인 ?? 0),
        어린이: Number(row.어린이 ?? 0),
        영유아: Number(row.영유아 ?? 0),
      })) : [],
    });
    {
      const savedZero = parseZeroFlag(i.일정표0원표시);
      const escortZero = parseEscortFlag(i.인솔자수);
      if (savedZero != null) {
        setZeroPriceToProduct(savedZero);
      } else if (escortZero != null) {
        setZeroPriceToProduct(escortZero);
      } else {
        const ls = readZeroFlagLocal(consultNo || i.상담번호);
        if (ls != null) setZeroPriceToProduct(ls);
      }
    }
    // 상세 응답 내에 상담상태 코드 필드가 있을 경우 상태에 반영 (예: i.상담상태, i.상담상태코드 등)
    if (i.상담상태코드 != null) {
      setConsultStatusCode(String(i.상담상태코드));
    } else if (i.상담상태 != null) {
      // 코드값이 직접 내려오는 경우를 대비
      setConsultStatusCode(String(i.상담상태));
    }

    // 팀컬러(상품번호 매칭키) 기반으로 상품번호 복원
    if ((currentProductNo == null || Number(currentProductNo) <= 0) && (navProductNo == null || Number(navProductNo) <= 0)) {
      const tc = i.팀컬러;
      const first = Array.isArray(tc) ? tc[0] : tc;
      const digits = first != null ? String(first).replace(/[^0-9]/g, '') : '';
      const n = Number(digits);
      if (Number.isFinite(n) && n > 0) {
        setCurrentProductNo(n);
      }
    }
  }, [consultInfo]);

  // 상담상태 코드 → 라벨/색상 매핑
  const getConsultStatusMeta = (code) => {
    const c = String(code || '');
    switch (c) {
      case '420003':
        return { label: '상담요청', colorClass: 'bg-blue-50 text-blue-700 border-blue-200' };
      case '420001':
        return { label: '진행중', colorClass: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
      case '420002':
        return { label: '예약전환', colorClass: 'bg-indigo-50 text-indigo-700 border-indigo-200' };
      case '420999':
        return { label: '상담취소', colorClass: 'bg-red-50 text-red-700 border-red-200' };
      default:
        return { label: '상담상태 미지정', colorClass: 'bg-gray-50 text-gray-600 border-gray-200' };
    }
  };

  // 공개 견적 응찰 상태 → 라벨/색상 매핑 (null 이면 공개 견적 참여건이 아니므로 미표시)
  const getBidStatusMeta = (status) => {
    switch (String(status || '')) {
      case 'won':
        return { label: '낙찰', colorClass: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
      case 'lost':
        return { label: '유찰', colorClass: 'bg-red-50 text-red-700 border-red-200' };
      case 'submitted':
      case 'bidding':
        return { label: '응찰중', colorClass: 'bg-amber-50 text-amber-700 border-amber-200' };
      default:
        return null;
    }
  };

  const saveConsultStatusChange = async (nextCode) => {
    if (!consultNo) {
      alert('상담번호를 확인할 수 없습니다.');
      return false;
    }
    const normalizedCode = nextCode || null;
    const prevCode = String(consultStatusCode || '');
    if (String(normalizedCode || '') === prevCode) {
      return true;
    }

    try {
      setStatusSaving(true);
      await saveConsultInfo({
        consultNo,
        productNo: effectiveProductNo,
        form,
        consultStatusCode: normalizedCode,
        consultTypeCode: '419002',
        zeroPriceToProduct,
      });
      setConsultStatusCode(normalizedCode);
      setConsultInfo((prev) => (
        prev
          ? { ...prev, 상담상태코드: normalizedCode, 상담상태: normalizedCode }
          : prev
      ));
      return true;
    } catch (e) {
      console.error('상담상태 저장 실패:', e);
      alert(e?.message || '상담상태 저장에 실패했습니다.');
      return false;
    } finally {
      setStatusSaving(false);
    }
  };

  // Format numbers with commas for display
  const fmt = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const num = Number(v);
    if (!Number.isFinite(num)) return '';
    return num.toLocaleString('ko-KR');
  };
  const parseNumberInput = (value) => {
    const cleaned = String(value || '').replace(/[^0-9.-]/g, '');
    if (cleaned === '' || cleaned === '-' || cleaned === '.') return 0;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  const onChange = (key, value) => {
    setForm(prev => {
      let next = { ...prev, [key]: value };
      if (key === '소아수' && Number(value) <= 0) {
        next.소아수 = 0;
        next.소아판매가 = 0;
        next.소아원가 = 0;
        if (Array.isArray(prev.세부원가목록)) {
          next.세부원가목록 = prev.세부원가목록.map(item => ({ ...item, 어린이: 0 }));
        }
      }
      if (key === '유아수' && Number(value) <= 0) {
        next.유아수 = 0;
        next.유아판매가 = 0;
        next.유아원가 = 0;
        if (Array.isArray(prev.세부원가목록)) {
          const base = next.세부원가목록 || prev.세부원가목록;
          next.세부원가목록 = base.map(item => ({ ...item, 영유아: 0 }));
        }
      }
      return next;
    });
  };

  const filteredAgencyList = useMemo(() => {
    const q = String(agencySearch || '').toLowerCase().trim();
    if (!q) return agencyList;
    return agencyList.filter((c) => {
      const name = String(c?.코드명 || c?.거래처명 || '').toLowerCase();
      const email = String(c?.대표이메일 || '').toLowerCase();
      const owner = String(c?.대표자명 || '').toLowerCase();
      const manager = String(c?.담당자명 || '').toLowerCase();
      const code = String(c?.코드 || c?.업체번호2 || c?.거래처번호 || '').toLowerCase();
      return name.includes(q) || email.includes(q) || owner.includes(q) || manager.includes(q) || code.includes(q);
    });
  }, [agencyList, agencySearch]);

  const ensureAgencyLoaded = async () => {
    if (agencyList.length > 0 || agencyLoading) return;
    try {
      setAgencyLoading(true);
      setAgencyError('');
      const { companyNo } = getUserInfo() || {};
      if (!companyNo) throw new Error('업체번호가 없습니다.');
      const list = await getAgencyList({ companyNo });
      setAgencyList(Array.isArray(list) ? list : []);
    } catch (e) {
      console.warn('판매거래처 목록 조회 실패:', e);
      setAgencyError('거래처 목록을 불러오지 못했습니다.');
    } finally {
      setAgencyLoading(false);
    }
  };

  const selectAgency = (c) => {
    const agencyNo = String(c?.코드 ?? c?.업체번호2 ?? c?.거래처번호 ?? '').trim();
    const agencyName = String(c?.코드명 ?? c?.거래처명 ?? '').trim();
    setForm((prev) => ({
      ...prev,
      판매업체: agencyName,
      판매업체번호: agencyNo,
    }));
    setAgencySearch('');
    setShowAgencyPicker(false);
  };

  const clearAgency = () => {
    setForm((prev) => ({
      ...prev,
      판매업체: '',
      판매업체번호: '',
    }));
    setAgencySearch('');
    setShowAgencyPicker(false);
  };

  useEffect(() => {
    if (!showAgencyPicker) return undefined;
    const onDocMouseDown = (e) => {
      if (agencyDropdownRef.current && !agencyDropdownRef.current.contains(e.target)) {
        setShowAgencyPicker(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [showAgencyPicker]);

  const openCostItemModal = () => {
    const base = Array.isArray(form.세부원가목록) ? form.세부원가목록 : [];
    setCostDraftList(base.map(row => ({
      원가항목: row?.원가항목 ?? '',
      성인: Number(row?.성인 ?? 0),
      어린이: Number(row?.어린이 ?? 0),
      영유아: Number(row?.영유아 ?? 0),
    })));
    setCostItemModalOpen(true);
  };

  const addCostDraftItem = () => {
    setCostDraftList(prev => ([...(Array.isArray(prev) ? prev : []), { 원가항목: '', 성인: 0, 어린이: 0, 영유아: 0 }]));
  };

  const updateCostDraftItem = (index, field, value) => {
    setCostDraftList(prev => {
      const list = Array.isArray(prev) ? [...prev] : [];
      if (!list[index]) return prev;
      const v = field === '원가항목' ? String(value) : Number(String(value).replace(/[^0-9.-]/g, '')) || 0;
      list[index] = { ...list[index], [field]: v };
      return list;
    });
  };

  const removeCostDraftItem = (index) => {
    setCostDraftList(prev => (Array.isArray(prev) ? prev : []).filter((_, i) => i !== index));
  };

  const saveCostDraft = () => {
    const normalized = (Array.isArray(costDraftList) ? costDraftList : []).map(row => ({
      원가항목: row?.원가항목 ?? '',
      성인: Number(row?.성인 ?? 0),
      어린이: Number(form.소아수 || 0) > 0 ? Number(row?.어린이 ?? 0) : 0,
      영유아: Number(form.유아수 || 0) > 0 ? Number(row?.영유아 ?? 0) : 0,
    }));
    setForm(prev => ({ ...prev, 세부원가목록: normalized }));
    setCostItemModalOpen(false);
  };

  const cancelCostDraft = () => {
    setCostItemModalOpen(false);
  };

  const aggregatedPerPersonCost = useMemo(() => {
    const list = Array.isArray(form.세부원가목록) ? form.세부원가목록 : [];
    return list.reduce((acc, cur) => {
      acc.성인 += Number(cur.성인 || 0);
      acc.어린이 += Number(cur.어린이 || 0);
      acc.영유아 += Number(cur.영유아 || 0);
      return acc;
    }, { 성인: 0, 어린이: 0, 영유아: 0 });
  }, [form.세부원가목록]);

  const totalCostAmount = useMemo(() => {
    const a = (aggregatedPerPersonCost.성인 || 0) * (form.성인수 || 0);
    const c = (aggregatedPerPersonCost.어린이 || 0) * (form.소아수 || 0);
    const i = (aggregatedPerPersonCost.영유아 || 0) * (form.유아수 || 0);
    const fallback = (form.성인원가 || 0) * (form.성인수 || 0) + (form.소아원가 || 0) * (form.소아수 || 0) + (form.유아원가 || 0) * (form.유아수 || 0);
    const useItems = Array.isArray(form.세부원가목록) && form.세부원가목록.length > 0;
    return useItems ? (a + c + i) : fallback;
  }, [aggregatedPerPersonCost, form.성인수, form.소아수, form.유아수, form.성인원가, form.소아원가, form.유아원가, form.세부원가목록]);

  const totalSaleAmount = useMemo(() => {
    return (form.성인판매가 || 0) * (form.성인수 || 0) + (form.소아판매가 || 0) * (form.소아수 || 0) + (form.유아판매가 || 0) * (form.유아수 || 0);
  }, [form.성인판매가, form.소아판매가, form.유아판매가, form.성인수, form.소아수, form.유아수]);

  const totalProfitAmount = useMemo(() => {
    return totalSaleAmount - totalCostAmount;
  }, [totalSaleAmount, totalCostAmount]);

  // 파일명 타임스탬프 생성: 원본이름_YYYYMMDD_HHmmss.ext
  const makeTimestampedName = (name) => {
    try {
      const dot = name.lastIndexOf('.');
      const base = dot > -1 ? name.slice(0, dot) : name;
      const ext = dot > -1 ? name.slice(dot) : '';
      const d = new Date();
      const pad = (n)=> String(n).padStart(2,'0');
      const ts = `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
      return `${base}_${ts}${ext}`;
    } catch { return name; }
  };

  const ATTACH_PATH = '1/counseling/Schedule/';

  const AUTO_QUOTE_PREFIXES = ['TRP__', 'AUTOQUOTE_'];

  const getAttachmentFileName = (item) => {
    try {
      if (item?.파일명) return String(item.파일명);
      const p = item?.첨부파일경로 || item?.파일경로 || '';
      if (!p) return '';
      return String(p).split('/').pop() || '';
    } catch {
      return '';
    }
  };

  const isAutoGeneratedQuoteAttachment = (item) => {
    try {
      if (item?.자동생성) return true;
      const name = getAttachmentFileName(item);
      if (!name) return false;
      return AUTO_QUOTE_PREFIXES.some(prefix => name.startsWith(prefix));
    } catch {
      return false;
    }
  };

  const isHtmlAttachmentName = (name) => {
    try {
      return /\.html?$/i.test(String(name || ''));
    } catch {
      return false;
    }
  };

  // Resolve a usable URL for an attachment item
  const resolveAttachmentUrl = (item) => {
    try {
      if (item?.url) return String(item.url);
      const p = item?.첨부파일경로 || item?.파일경로 || '';
      if (!p) return null;
      const path = String(p).replace(/^\/+/, '');
      return `https://kr.object.iwinv.kr/orgin/${path}`;
    } catch { return null; }
  };

  const getLatestAutoPdfUrl = () => {
    try {
      const list = Array.isArray(form.견적첨부파일리스트)
        ? form.견적첨부파일리스트.filter(f => f && isAutoGeneratedQuoteAttachment(f))
        : [];
      if (!list.length) return '';
      const last = list[list.length - 1];
      return resolveAttachmentUrl(last) || '';
    } catch { return ''; }
  };

  const onOpenQuoteEmail = () => {
    const url = getLatestAutoPdfUrl();
    if (!url) {
      alert('자동 생성된 견적서 PDF가 없습니다. 먼저 PDF를 생성/첨부하세요.');
      return;
    }
    setEmailPdfUrl(url);
    setEmailOpen(true);
  };

  const emailAttachmentList = useMemo(() => {
    try {
      const list = Array.isArray(form.견적첨부파일리스트) ? form.견적첨부파일리스트 : [];
      if (!list.length) return [];

      const currentPdfUrl = String(emailPdfUrl || '').trim();
      if (!currentPdfUrl) return list;

      return list.filter((it) => {
        try {
          const u = resolveAttachmentUrl(it);
          return u && u !== currentPdfUrl;
        } catch {
          return true;
        }
      });
    } catch {
      return [];
    }
  }, [form.견적첨부파일리스트, emailPdfUrl]);

  const onSelectFile = (e) => {
    const inputEl = e?.target;
    const f = inputEl?.files?.[0];
    if (!f) return;
    if (uploading) return;

    // 파일 선택 즉시 업로드 + 저장까지 한번에 처리
    (async () => {
      try {
        if (!consultNo) {
          alert('상담번호를 확인할 수 없습니다.');
          return;
        }
        if (!effectiveProductNo) {
          alert('상품번호를 확인할 수 없습니다.');
          return;
        }

        setUploading(true);
        const stamped = makeTimestampedName(f.name);
        const { url } = await uploadS3({ file: f, fileName: stamped, filePath: ATTACH_PATH });

        // 저장용 경로는 도메인 제외 경로
        const pathOnly = `${ATTACH_PATH}${stamped}`;
        const newItem = { 첨부파일번호: 0, 파일명: stamped, 첨부파일경로: pathOnly, url, 자동생성: false };
        const nextForm = {
          ...form,
          견적첨부파일리스트: Array.isArray(form.견적첨부파일리스트)
            ? [...form.견적첨부파일리스트, newItem]
            : [newItem],
        };

        setForm(nextForm);

        // 업로드 완료 후 곧바로 상담정보 저장(첨부파일 반영)
        await saveConsultInfo({
          consultNo,
          productNo: effectiveProductNo,
          form: nextForm,
          consultStatusCode,
          consultTypeCode: '419002',
          zeroPriceToProduct,
        });
      } catch (err) {
        console.error('파일 업로드/저장 실패:', err);
        alert('파일 업로드/저장에 실패했습니다.');
      } finally {
        setUploading(false);
        try { if (inputEl) inputEl.value = ''; } catch { /* ignore */ }
      }
    })();
  };

  const onRemoveAttachment = (idx) => {
    (async () => {
      try {
        if (!consultNo) {
          alert('상담번호를 확인할 수 없습니다.');
          return;
        }
        if (!effectiveProductNo) {
          alert('상품번호를 확인할 수 없습니다.');
          return;
        }

        const nextForm = {
          ...form,
          견적첨부파일리스트: (Array.isArray(form.견적첨부파일리스트) ? form.견적첨부파일리스트 : []).filter((_, i) => i !== idx),
        };

        setForm(nextForm);

        setSaving(true);
        await saveConsultInfo({
          consultNo,
          productNo: effectiveProductNo,
          form: nextForm,
          consultStatusCode,
          consultTypeCode: '419002',
          zeroPriceToProduct,
        });
      } catch (e) {
        console.error('첨부파일 삭제/저장 실패:', e);
        alert(e?.message || '첨부파일 삭제/저장에 실패했습니다.');
      } finally {
        setSaving(false);
      }
    })();
  };

  const onDeleteConsult = async () => {
    try {
      if (!consultNo) { alert('상담번호가 없습니다.'); return; }

      const linkedProductNo = effectiveProductNo != null && Number(effectiveProductNo) > 0
        ? Number(effectiveProductNo)
        : null;

      const msg = linkedProductNo
        ? `해당 견적(상담)을 삭제하시겠습니까?\n\n연결된 상품(상품번호: ${linkedProductNo})도 함께 삭제됩니다.\n이 작업은 되돌릴 수 없습니다.`
        : '해당 견적(상담)을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.';

      const ok = window.confirm(msg);
      if (!ok) return;
      setLoading(true);

      await deleteConsult({ consultNo });

      if (linkedProductNo) {
        try {
          await deleteTourProduct({ productNo: linkedProductNo });
          alert('견적(상담)과 연결 상품이 삭제되었습니다.');
        } catch (productErr) {
          console.error('연결 상품 삭제 실패:', productErr);
          alert(`견적(상담)은 삭제되었으나 연결된 상품 삭제에 실패했습니다.\n상품번호: ${linkedProductNo}\n\n${(productErr && (productErr.apiMessage || productErr.message)) || ''}`.trim());
        }
      } else {
        alert('삭제되었습니다.');
      }

      navigate(-1);
    } catch (e) {
      console.error('견적 삭제 실패:', e);
      alert((e && (e.apiMessage || e.message)) || '견적 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const onDeleteLinkedProductOnly = async () => {
    try {
      if (!consultNo) { alert('상담번호가 없습니다.'); return; }

      const linkedProductNo = effectiveProductNo != null && Number(effectiveProductNo) > 0
        ? Number(effectiveProductNo)
        : null;

      if (!linkedProductNo) {
        alert('연결된 상품이 없습니다.');
        return;
      }

      const ok = window.confirm(
        `연결된 상품만 삭제하시겠습니까?\n\n상품번호: ${linkedProductNo}\n\n삭제 후 이 상담은 상품이 연결되지 않은 상태가 되며, 새 상품을 생성/연결할 수 있습니다.\n이 작업은 되돌릴 수 없습니다.`
      );
      if (!ok) return;

      setLoading(true);

      await updateConsultProductNo({ consultNo, productNo: '' });

      setConsultInfo((prev) => {
        if (!prev) return prev;
        return { ...prev, 팀컬러: [] };
      });

      setCurrentProductNo(null);
      setPriceNo(null);
      setAutoProductCreated(true);
      try {
        sessionStorage.removeItem('order_product_no_-');
        sessionStorage.removeItem('order_product_name_-');
      } catch {}

      navigate('/quote-detail/-', {
        state: {
          ...(navState || {}),
          consultNo,
          productNo: null,
          productName: navProductName,
        },
        replace: true,
      });

      try {
        await deleteTourProduct({ productNo: linkedProductNo });
      } catch (e) {
        console.error('연결 상품 삭제 실패:', e);
        const msg = (e && (e.apiMessage || e.message)) || '';
        const relink = window.confirm(
          `연결 상품 삭제에 실패했습니다.\n\n상품번호: ${linkedProductNo}\n${msg ? `\n${msg}\n` : ''}\n\n기존 상품을 다시 연결하시겠습니까?`
        );
        if (relink) {
          try {
            await updateConsultProductNo({ consultNo, productNo: linkedProductNo });
            setCurrentProductNo(linkedProductNo);
            navigate(`/quote-detail/${linkedProductNo}`, {
              state: {
                ...(navState || {}),
                consultNo,
                productNo: linkedProductNo,
                productName: navProductName,
              },
              replace: true,
            });
          } catch (relinkErr) {
            console.error('상품 재연결 실패:', relinkErr);
            alert(
              `상품 재연결에도 실패했습니다.\n상품번호: ${linkedProductNo}\n\n${(relinkErr && (relinkErr.apiMessage || relinkErr.message)) || ''}`.trim()
            );
          }
        }
        return;
      }

      try {
        const info = await getConsultInfo({ consultNo });
        setConsultInfo(info || null);
      } catch {}

      alert('연결된 상품이 삭제되었습니다. 이제 새 상품을 생성/연결할 수 있습니다.');
    } catch (e) {
      console.error('연결 상품 삭제 처리 실패:', e);
      alert((e && (e.apiMessage || e.message)) || '연결 상품 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 견적 정보를 기반으로 상품 생성
  const onCreateProduct = async () => {
    try {
      // 필수 정보 검증
      if (!form.견적명?.trim()) {
        alert('상품명(견적명)을 입력해주세요.');
        return;
      }
      if (!form.시작일 || !form.종료일) {
        alert('여행 기간(시작일/종료일)을 입력해주세요.');
        return;
      }

      setCreatingProduct(true);
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        throw new Error('로그인 정보를 확인할 수 없습니다.');
      }

      // 소요일 계산
      let duration = 1;
      try {
        const start = new Date(form.시작일);
        const end = new Date(form.종료일);
        const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        duration = diff > 0 ? diff : 1;
      } catch {}

      const picked = await openCityPicker({ companyNo, initialCityCode: cityPickSelectedCode });
      if (!picked || !picked.cityNo) {
        setCreatingProduct(false);
        return;
      }
      const cityNo = String(picked.cityNo);
      const ok = window.confirm(`선택한 도시로 새 상품을 생성하시겠습니까?\n\n도시: ${picked.cityName || '-'} (${cityNo})`);
      if (!ok) {
        setCreatingProduct(false);
        return;
      }

      // 상품 생성 API 호출
const productResp = await addTourProduct({
        supplierNo: companyNo,
        userNo,
        companyNo,
        productName: form.견적명.trim(),
        cityNo,
        durationValue: String(duration),
        durationUnitCode: '012001', // 일
        currencyCode: '058001', // KRW
        productTypeCode: '309299', // 기타
      });

      const productRow = Array.isArray(productResp?.table) ? productResp.table[0] : null;
      const newProductNo = productRow?.상품번호 || productRow?.ProductNo;

      if (!newProductNo) {
        throw new Error('상품 생성 결과를 확인할 수 없습니다.');
      }

      await autoRegisterRepresentativeImagesFromCity({
        companyNo,
        userNo,
        productNo: newProductNo,
        cityNo,
      });

      // 견적과 상품번호 매칭 (상담정보 업데이트)
      if (consultNo) {
        try {
          await updateConsultProductNo({ consultNo, productNo: newProductNo });
} catch (e) {
          console.warn('[상품 매칭] 실패:', e);
        }
      }

      // 상태 업데이트
      setCurrentProductNo(newProductNo);
      
      alert(`상품이 성공적으로 생성되었습니다.\n상품번호: ${newProductNo}`);
      
      // 페이지 새로고침하여 새 상품번호로 이동
      navigate(`/quote-detail/${newProductNo}`, {
        state: {
          consultNo,
          productNo: newProductNo,
          productName: form.견적명.trim(),
        },
        replace: true,
      });
    } catch (e) {
      console.error('[상품 생성] 실패:', e);
      alert(e?.message || '상품 생성에 실패했습니다.');
    } finally {
      setCreatingProduct(false);
    }
  };

  const onSave = async (opts = {}) => {
    if (!consultNo) { alert('상담번호를 확인할 수 없습니다.'); return; }
    if (!form.시작일) { alert('시작일을 입력해주세요.'); return; }

    // opts.syncToProduct: 진입 시 자동 연결은 상품번호만 저장하고 가격/재고는 건너뜀
    const doSyncToProduct = opts.syncToProduct !== undefined ? !!opts.syncToProduct : syncToProduct;
    const quiet = !!opts.quiet;
    
    try {
      setSaving(true);
      setError(null);

      let productNoForSave = effectiveProductNo;
      let createdProductNo = null;

      // 상품번호가 없으면 저장 시점에 강제로 상품 생성 후 이어서 저장
      if (!productNoForSave) {
        try {
          setCreatingProduct(true);
          const { companyNo, userNo } = getUserInfo() || {};
          if (!companyNo || !userNo) {
            alert('사용자 정보를 확인할 수 없습니다. 다시 로그인 후 시도해주세요.');
            return;
          }

          const name = (form?.견적명 || consultInfo?.견적명 || '').trim();
          if (!name) {
            alert('상품을 생성하려면 견적명을 입력해주세요.');
            return;
          }

          // 소요일 계산 (종료일이 없으면 1일)
          let duration = 1;
          try {
            const start = new Date(form?.시작일);
            const end = form?.종료일 ? new Date(form?.종료일) : null;
            if (end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
              const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
              duration = diff > 0 ? diff : 1;
            }
          } catch {}

          const picked = await openCityPicker({ companyNo, initialCityCode: cityPickSelectedCode });
          if (!picked || !picked.cityNo) {
            return;
          }
          const cityNo = String(picked.cityNo);

          const productResp = await addTourProduct({
            supplierNo: companyNo,
            userNo,
            companyNo,
            productName: name,
            cityNo,
            durationValue: String(duration),
            durationUnitCode: '012001',
            currencyCode: '058001',
            productTypeCode: '309299',
          });

          const productRow = Array.isArray(productResp?.table) ? productResp.table[0] : null;
          const newProductNo = productRow?.상품번호 || productRow?.ProductNo;
          if (!newProductNo) {
            alert('상품 생성에 실패했습니다. (상품번호 없음)');
            return;
          }

          await autoRegisterRepresentativeImagesFromCity({
            companyNo,
            userNo,
            productNo: newProductNo,
            cityNo,
            logLabel: '[저장 시 자동 상품 생성]',
          });

          try {
            await updateConsultProductNo({ consultNo, productNo: newProductNo });
          } catch (e) {
            console.warn('[저장 시 자동 상품 매칭] 실패:', e);
          }

          createdProductNo = Number(newProductNo);
          productNoForSave = createdProductNo;
          setCurrentProductNo(createdProductNo);
          setPriceNo(null);

          // 공개 견적 요청에서 이관된 상담이면, 대리점이 실어 보낸 일정 스마트 등록
          // JSON(estimate_extras.itinerary)으로 신규 상품의 일정표를 자동 생성한다.
          // 일정 생성 실패는 상담/상품 저장을 막지 않는다(정보는 남고 재시도 가능).
          try {
            const extras = await getEstimateExtras(consultNo);
            const structuredItinerary = extras?.itinerary;
            if (Array.isArray(structuredItinerary) && structuredItinerary.length) {
              const result = await registerItineraryFromJson({
                productNo: createdProductNo,
                companyNo,
                structuredItinerary,
                jsonLineRoles: extras?.jsonLineRoles || {},
                userNo,
              });
              console.info('[공개견적 일정 자동생성]', result);
            }
          } catch (itinErr) {
            console.warn('[공개견적 일정 자동생성] 실패(무시):', itinErr);
          }
        } finally {
          setCreatingProduct(false);
        }
      }

      if (!productNoForSave) {
        alert('상품번호를 확인할 수 없습니다.');
        return;
      }
      
      // 0. 예약현황에서 취소가 아닌 첫 번째 주문번호 추출 (조인업체명으로 사용)
      let joinCompanyName = '';
      if (!createdProductNo) {
        try {
          // 1년 전부터 1년 후까지의 예약현황 조회
          const today = new Date();
          const oneYearAgo = new Date(today);
          oneYearAgo.setFullYear(today.getFullYear() - 1);
          const oneYearLater = new Date(today);
          oneYearLater.setFullYear(today.getFullYear() + 1);
          const pad = (n) => String(n).padStart(2, '0');
          const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

          const orderList = await getOrderStatusListByProductNo({
            startDate: toYmd(oneYearAgo),
            endDate: toYmd(oneYearLater),
            productNo: productNoForSave,
          });

          if (Array.isArray(orderList) && orderList.length > 0) {
            // 단체번호별로 그룹화
            const groupedByGroupNo = new Map();
            for (const order of orderList) {
              const groupNo = order?.행사단체번호 != null ? String(order.행사단체번호) : '기타';
              if (!groupedByGroupNo.has(groupNo)) {
                groupedByGroupNo.set(groupNo, []);
              }
              groupedByGroupNo.get(groupNo).push(order);
            }
            
            // 각 단체에서 취소가 아닌 첫 번째 주문번호 찾기
            for (const [, orders] of groupedByGroupNo) {
              for (const order of orders) {
                const status = String(order?.예약상태2 || '');
                const isCancelled = status.includes('취소완료') || 
                                    status.includes('취소확정') || 
                                    status.includes('취소접수') ||
                                    status.includes('취소 접수');
                if (!isCancelled) {
                  const orderNo = order?.주문번호 || order?.orderNo;
                  if (orderNo) {
                    joinCompanyName = String(orderNo);
                    break;
                  }
                }
              }
              if (joinCompanyName) break;
            }
          }
        } catch (orderErr) {
          console.warn('[조인업체명 추출] 예약현황 조회 실패:', orderErr);
        }
      }
      
      // 1. 상담정보 저장 (첨부파일 포함 + 상담상태/상담구분 코드 + 조인업체명)
      // 상담구분은 현재 고정값(419002: 일반 상담)으로 전달
      await saveConsultInfo({
        consultNo,
        productNo: productNoForSave,
        form,
        consultStatusCode,
        consultTypeCode: '419002',
        joinCompanyName,
        zeroPriceToProduct,
      });
      
      // 2. 상품 가격/재고에도 저장 (옵션 선택 시 — 자동 상품연결 시에는 스킵)
      if (doSyncToProduct) {
// priceNo가 없으면 신규 생성 모드
        if (!priceNo) {
} else {
}
        
        try {
          const { userNo, companyNo } = getUserInfo() || {};
          let basePriceInfo = null;
          
          // 기존 가격정보 조회 (priceNo가 있을 때만)
          if (priceNo) {
            try {
              const baseData = await getPriceReservationInfo({ 
                productNo: productNoForSave, 
                companyNo, 
                priceNo 
              });
              const baseCandidates = [baseData?.table, baseData?.table1, baseData?.Table, baseData?.Table1, baseData?.Table0];
              let baseArr = [];
              for (const c of baseCandidates) { if (Array.isArray(c) && c.length) { baseArr = c; break; } }
              const baseRow = baseArr.length ? baseArr[0] : null;
              
              if (baseRow?.가격정보) {
                try {
                  const parsed = JSON.parse(baseRow.가격정보);
                  basePriceInfo = Array.isArray(parsed) && parsed.length ? parsed[0] : parsed;
                  
                  // 불필요한 필드 제거
                  if (basePriceInfo) {
                    // keep existing 적용환율 as-is (allow multi-currency)
                  }
} catch(_) {}
              }
            } catch (err) {
              console.warn('[상품가격 저장] 기존 가격정보 조회 실패:', err);
            }
          }
          
          // 신규 생성인 경우 템플릿 가격정보 조회(@가격번호=0)로 적용환율/일정 코드를 보강
          let templateInfo = null;
          if (!priceNo) {
            try {
              const tData = await getPriceReservationInfo({
                productNo: productNoForSave,
                companyNo,
                priceNo: 0,
              });
              const tCandidates = [tData?.table, tData?.table1, tData?.Table, tData?.Table1, tData?.Table0];
              let tArr = [];
              for (const c of tCandidates) { if (Array.isArray(c) && c.length) { tArr = c; break; } }
              const tRow = tArr.length ? tArr[0] : null;
              if (tRow?.가격정보) {
                const parsedT = JSON.parse(tRow.가격정보);
                templateInfo = Array.isArray(parsedT) && parsedT.length ? parsedT[0] : parsedT;
              }
            } catch (e) {
              console.warn('[상품가격 저장] 템플릿 가격정보 조회 실패:', e);
            }
          }

          // 기본 구조 생성 (정상 페이로드 구조 반영)
          const priceInfo = basePriceInfo || {
            상품번호: Number(productNoForSave),
            전일종가: 1,
            상품일정타입: 'Multi Day Tour',
            상품일정타입코드: '085002',
            통화코드: '058001',
            통화기호: '￦',
            적용환율: Array.isArray(templateInfo?.적용환율) && templateInfo.적용환율.length
              ? templateInfo.적용환율
              : [ { 통화코드: '058001', 통화기호: '￦', 적용환율: 1, 실시간환율: 1, 평균환율: 1 } ],
            노출채널: [
              { 선택: true, 코드: '웹노출여부', 코드명: '웹노출여부' },
              { 선택: true, 코드: '대리점노출여부', 코드명: '대리점노출여부' }
            ],
            랜드사: [],
            판매마감일기준: '1',
            판매마감특정일: '',
            판매마감일디폴트몇일전: 0,
            가상모객사용: false,
            가상모객인원: 0,
            가상모객출발몇일전: 0,
            원가구분: '2',
            판매가: [],
            총원가: [],
            세부원가: [],
            특별약관사용: false,
            특별약관간략정보: '',
            취소료: [],
            행사시작시간: null,
            선택시작시간: [{ 시간: '00:00' }],
            적용기간: [],
            플랫폼수수료적용여부: true,
            플랫폼수수료: 0.01,
            결제수수료적용여부: true,
            결제수수료: 0.03,
            기존데이터업데이트여부: true,
            합산가격여부: true,
            커미션타입: '정률',
            선택요금제: [],
            요금제: [],
            상품자원요금제: [],
            선택일정타입: (templateInfo && Number(templateInfo.선택일정타입) > 0)
              ? Number(templateInfo.선택일정타입)
              : (Array.isArray(templateInfo?.일정타입) && templateInfo.일정타입[0]?.코드
                  ? Number(templateInfo.일정타입[0].코드)
                  : 47611),
            일정타입: (Array.isArray(templateInfo?.일정타입) && templateInfo.일정타입.length)
              ? templateInfo.일정타입
              : [{ 선택: false, 코드: 47611, 코드명: '대표일정' }],
          };
          
          // 상품자원요금제, 선택요금제, 요금제 데이터 보강
          try {
// 신규 생성 시 템플릿 기반으로 선반영 (향후 보강 로직이 덮어씌움)
            if (!priceNo && templateInfo) {
              if ((!priceInfo.상품자원요금제 || priceInfo.상품자원요금제.length === 0) && Array.isArray(templateInfo.상품자원요금제)) {
                priceInfo.상품자원요금제 = templateInfo.상품자원요금제;
              }
              if ((!priceInfo.선택요금제 || priceInfo.선택요금제.length === 0) && Array.isArray(templateInfo.선택요금제)) {
                priceInfo.선택요금제 = templateInfo.선택요금제;
              }
              if ((!priceInfo.요금제 || priceInfo.요금제.length === 0) && Array.isArray(templateInfo.요금제)) {
                priceInfo.요금제 = templateInfo.요금제;
              }
            }
            const enhancedData = await getEnhancedPriceDataForQuote({
              productNo: productNoForSave,
              companyNo,
              priceNo
            });
            
            // 보강된 데이터로 업데이트 (기존 데이터가 없거나 비어있을 때만)
            if (!priceInfo.상품자원요금제 || priceInfo.상품자원요금제.length === 0) {
              priceInfo.상품자원요금제 = enhancedData.상품자원요금제;
            }
            if (!priceInfo.선택요금제 || priceInfo.선택요금제.length === 0) {
              priceInfo.선택요금제 = enhancedData.선택요금제;
            }
            if (!priceInfo.요금제 || priceInfo.요금제.length === 0) {
              priceInfo.요금제 = enhancedData.요금제;
            }
} catch (enhanceErr) {
            console.warn('[상품가격 저장] 데이터 보강 실패, 기본값 사용:', enhanceErr);
            // 신규 생성 시 최소한의 요금제 정보라도 설정
            if (!priceNo) {
              // 템플릿 값이 있으면 사용, 없으면 빈 배열 유지
              if ((!priceInfo.상품자원요금제 || priceInfo.상품자원요금제.length === 0) && Array.isArray(templateInfo?.상품자원요금제)) {
                priceInfo.상품자원요금제 = templateInfo.상품자원요금제;
              }
              if ((!priceInfo.선택요금제 || priceInfo.선택요금제.length === 0) && Array.isArray(templateInfo?.선택요금제)) {
                priceInfo.선택요금제 = templateInfo.선택요금제;
              }
              if ((!priceInfo.요금제 || priceInfo.요금제.length === 0) && Array.isArray(templateInfo?.요금제)) {
                priceInfo.요금제 = templateInfo.요금제;
              }
            }
          }

          // 추가 정규화: 누락 필드 보강 (템플릿/보강 데이터 기반)
          // 1) 통화코드/통화기호 보강
          if (!priceInfo.통화코드) priceInfo.통화코드 = templateInfo?.통화코드 || '058001';
          if (!priceInfo.통화기호) priceInfo.통화기호 = templateInfo?.통화기호 || '￦';

          // 2) 일정타입/선택일정타입 보강
          if (!Array.isArray(priceInfo.일정타입) || priceInfo.일정타입.length === 0) {
            if (Array.isArray(templateInfo?.일정타입) && templateInfo.일정타입.length) {
              priceInfo.일정타입 = templateInfo.일정타입;
            } else {
              priceInfo.일정타입 = [{ 선택: false, 코드: 47611, 코드명: '대표일정' }];
            }
          }
          if (!priceInfo.선택일정타입 || Number(priceInfo.선택일정타입) <= 0) {
            if (templateInfo && Number(templateInfo.선택일정타입) > 0) {
              priceInfo.선택일정타입 = Number(templateInfo.선택일정타입);
            } else if (Array.isArray(priceInfo.일정타입) && priceInfo.일정타입[0]?.코드) {
              priceInfo.선택일정타입 = Number(priceInfo.일정타입[0].코드);
            } else {
              priceInfo.선택일정타입 = 47611;
            }
          }

          // 3) 선택요금제/요금제 보강
          const ensureRatePlanFromResources = () => {
            // 상품자원요금제에서 첫 요금제리스트의 가격옵션번호를 선택요금제로 설정
            if (Array.isArray(priceInfo.상품자원요금제) && priceInfo.상품자원요금제.length > 0) {
              for (const res of priceInfo.상품자원요금제) {
                const list = Array.isArray(res?.요금제리스트) ? res.요금제리스트 : [];
                if (list.length > 0 && list[0]?.가격옵션번호) {
                  const planNo = Number(list[0].가격옵션번호);
                  priceInfo.선택요금제 = [{ 가격옵션번호: planNo, 합산가격여부: 'N' }];
                  // 요금제 배열에 존재하지 않으면 추가
                  const exists = Array.isArray(priceInfo.요금제) && priceInfo.요금제.some(p => Number(p.요금제번호) === planNo);
                  if (!exists) {
                    if (!Array.isArray(priceInfo.요금제)) priceInfo.요금제 = [];
                    priceInfo.요금제.push({ 요금제번호: planNo, 요금제명: list[0].가격옵션명 || '기본요금제' });
                  }
                  return true;
                }
              }
            }
            return false;
          };
          if (!Array.isArray(priceInfo.선택요금제) || priceInfo.선택요금제.length === 0) {
            if (!(Array.isArray(templateInfo?.선택요금제) && templateInfo.선택요금제.length > 0)) {
              ensureRatePlanFromResources();
            }
          }
          if (!Array.isArray(priceInfo.요금제) || priceInfo.요금제.length === 0) {
            // 템플릿에 없고 선택요금제만 있다면 요금제 구성 보강
            if (Array.isArray(priceInfo.선택요금제) && priceInfo.선택요금제.length > 0) {
              const planNo = Number(priceInfo.선택요금제[0].가격옵션번호 || 0);
              if (planNo > 0) {
                priceInfo.요금제 = [{ 요금제번호: planNo, 요금제명: '기본요금제' }];
              }
            }
          }
          
          // 판매가 업데이트 (판매가, Tax및유류할증료, 커미션 3개 항목 필수)
          const 판매가목록 = Array.isArray(priceInfo.판매가) ? [...priceInfo.판매가] : [];

          const productAdultSale = zeroPriceToProduct ? 0 : Number(form.성인판매가 || 0);
          const productChildSale = zeroPriceToProduct ? 0 : Number(form.소아판매가 || 0);
          const productInfantSale = zeroPriceToProduct ? 0 : Number(form.유아판매가 || 0);
          
          // 1. 판매가
          const 판매가Index = 판매가목록.findIndex(x => x.항목 === '판매가');
          const 판매가Row = {
            항목: '판매가',
            번호: 1,
            통화코드: priceInfo.통화코드 || '058001',
            통화기호: priceInfo.통화기호 || '￦',
            성인: String(productAdultSale),
            어린이: productChildSale,
            영유아: productInfantSale,
          };
          if (판매가Index >= 0) {
            판매가목록[판매가Index] = 판매가Row;
          } else {
            판매가목록.push(판매가Row);
          }
          
          // 2. Tax및유류할증료
          const 세금Index = 판매가목록.findIndex(x => x.항목 === 'Tax및유류할증료');
          const 세금Row = {
            항목: 'Tax및유류할증료',
            번호: 2,
            통화코드: priceInfo.통화코드 || '058001',
            통화기호: priceInfo.통화기호 || '￦',
            성인: 0,
            어린이: 0,
            영유아: 0,
          };
          if (세금Index >= 0) {
            판매가목록[세금Index] = 세금Row;
          } else {
            판매가목록.push(세금Row);
          }
          
          // 3. 커미션
          const 커미션Index = 판매가목록.findIndex(x => x.항목 === '커미션');
          const 커미션Row = {
            항목: '커미션',
            번호: 3,
            통화코드: priceInfo.통화코드 || '058001',
            통화기호: priceInfo.통화기호 || '￦',
            성인: 0,
            어린이: 0,
            영유아: 0,
          };
          if (커미션Index >= 0) {
            판매가목록[커미션Index] = 커미션Row;
          } else {
            판매가목록.push(커미션Row);
          }
          
          priceInfo.판매가 = 판매가목록;
          
          // 원가 업데이트
          priceInfo.총원가 = [{
            구분: '총원가',
            통화코드: priceInfo.통화코드 || '058001',
            통화기호: priceInfo.통화기호 || '￦',
            성인: Number(form.성인원가 || 0),
            어린이: Number(form.소아원가 || 0),
            영유아: Number(form.유아원가 || 0),
          }];
          
          // 세부원가 (원가구분 '2'일 때 통합원가 항목 추가)
          if (priceInfo.원가구분 === '2') {
            priceInfo.세부원가 = [{
              번호: 1,
              원가항목: '통합원가',
              항목: '통합원가',
              원가항목코드: '233013',
              통화코드: priceInfo.통화코드 || '058001',
              통화기호: priceInfo.통화기호 || '￦',
              환율적용방식: '전일종가',
              환율적용방식코드: '312001',
              적용환율: 1,
              성인: Number(form.성인원가 || 0),
              어린이: Number(form.소아원가 || 0),
              영유아: Number(form.유아원가 || 0),
            }];
          } else {
            priceInfo.세부원가 = [];
          }
          
          // 적용기간 설정: 항상 신규 생성 모드로 저장 (가격번호 제외)
          priceInfo.적용기간 = [{
            시작일: form.시작일,
            종료일: form.시작일,
            요일: ['월','화','수','목','금','토','일'],
            요일2: 'YYYYYYY',
          }];
// 상품번호 강제 설정
          priceInfo.상품번호 = Number(productNoForSave);
          
          // 일정타입 정보 추가 (기본값/템플릿 기반 유지)
          if (!priceInfo.선택일정타입) {
            priceInfo.선택일정타입 = 47611;
          }
          if (!priceInfo.일정타입 || priceInfo.일정타입.length === 0) {
            priceInfo.일정타입 = [{ 선택: false, 코드: 47611, 코드명: '대표일정' }];
          }
          
          // 기존 요금제 정보 보강 로직은 위의 getEnhancedPriceDataForQuote로 대체됨
          
          // 최종 정제: 불필요한 필드 제거 및 구조 검증
          
          
          // 행사시작시간은 null 유지 (성공 페이로드 참조)
          if (!priceInfo.행사시작시간) {
            priceInfo.행사시작시간 = null;
          }
          
          // 선택시작시간은 시간 객체 포함
          if (!Array.isArray(priceInfo.선택시작시간) || priceInfo.선택시작시간.length === 0) {
            priceInfo.선택시작시간 = [{ 시간: '00:00' }];
          }
          
          // 취소료는 빈 배열 (성공 페이로드 참조)
          if (!Array.isArray(priceInfo.취소료)) {
            priceInfo.취소료 = [];
          }
          
          // 합산가격여부는 성공 페이로드 기준으로 항상 true 강제
          priceInfo.합산가격여부 = true;
// 가격정보 저장
          const result = await saveTourPriceInfo({
            priceInfoJson: JSON.stringify(priceInfo),
            userNo,
            companyNo,
            합산가격여부: true,
          });
// 재고 저장 로직 추가
          try {
// 1. 상품 자원 리스트 조회 (자원번호 획득)
            const resourceList = await getTourProductResourceList({ 
              companyNo, 
              productNo: productNoForSave 
            });
// 2. 자원이 있으면 재고 저장
            if (Array.isArray(resourceList) && resourceList.length > 0) {
              const firstResource = resourceList[0];
              const resourceNo = firstResource.자원번호 || firstResource.resourceNo || firstResource.번호;
              
              if (resourceNo) {
                // 3. 인원수 합계를 재고로 설정
                const totalCount = (form.성인수 || 0) + (form.소아수 || 0) + (form.유아수 || 0);
                
                // 4. 시작일에 대한 재고 이벤트 생성
                const stockEvents = [{
                  날짜: form.시작일,
                  시간: '00:00',
                  자원번호: Number(resourceNo)
                }];
// 5. 재고 저장 API 호출
                const stockResult = await saveTourSelectedStock({
                  productNo: productNoForSave,
                  companyNo,
                  userNo,
                  stockCount: totalCount,
                  events: stockEvents,
                  calendarUse: true,
                  seriesUpdate: false
                });
} else {
                console.warn('[상품재고 저장] 자원번호를 찾을 수 없습니다.');
              }
            } else {
              console.warn('[상품재고 저장] 자원 리스트가 비어있습니다.');
            }
          } catch (stockErr) {
            console.error('[상품재고 저장] 실패:', stockErr);
            // 재고 저장 실패는 경고만 표시 (가격은 이미 저장됨)
          }
          
        } catch (priceErr) {
          console.error('[상품가격 저장] 실패:', priceErr);
          // 상품 가격 저장 실패해도 상담정보는 저장되었으므로 경고만 표시
          alert('상담정보는 저장되었으나, 상품 가격 저장에 실패했습니다.\n' + (priceErr?.message || ''));
        }
      }
      
      // 3. 최신 상태 반영
      // 서버가 일정표0원표시를 저장/반환하지 않으므로 저장 시점의 체크상태를 로컬에 확정 기록
      writeZeroFlagLocal(consultNo, zeroPriceToProduct);
      const info = await getConsultInfo({ consultNo });
      setConsultInfo(info || null);

      // 저장 후 URL/상태에 생성된 상품번호를 반영 (페이지 새로고침 없이)
      if (createdProductNo) {
        navigate(`/quote-detail/${createdProductNo}`, {
          state: {
            ...(navState || {}),
            consultNo,
            productNo: createdProductNo,
            productName: form?.견적명 || navProductName,
          },
          replace: true,
        });
      }
      if (!quiet) {
        alert(doSyncToProduct ? '상담정보 및 상품 가격/재고가 저장되었습니다.' : '상담정보가 저장되었습니다.');
      }
      
      // 4. 페이지 새로고침 없이 상태로 최신 정보 반영 완료
    } catch (e) {
      console.error('상담정보 저장 실패:', e);
      setError(e?.message || '상담정보 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const fields = useMemo(() => {
    const i = consultInfo || {};
    return {
      요청자명: i.요청자명 ?? '',
      요청자휴대폰: i.요청자휴대폰 ?? '',
      요청자이메일: i.요청자이메일 ?? '',
      시작일: i.시작일 ?? '',
      종료일: i.종료일 ?? '',
      견적내용: i.견적내용 ?? '',
      성인수: i.성인수 ?? 0,
      소아수: i.소아수 ?? 0,
      유아수: i.유아수 ?? 0,
    };
  }, [consultInfo]);

  const onFetchPrices = async () => {
    if (!effectiveProductNo) { alert('상품번호를 확인할 수 없습니다.'); return; }
    const date = form.시작일 || fields.시작일;
    if (!date) { alert('시작일을 먼저 선택해 주세요.'); return; }
    try {
      setSaving(true);
      
      // 1. 기본 가격 조회 (판매가만)
      const { adult, child, infant, rawRow, allZeroTotalSale } = await getQuotePrices({ productNo: effectiveProductNo, startDate: date });
      const resolved = resolveZeroFlag({ fieldVal: consultInfo?.일정표0원표시, escortVal: consultInfo?.인솔자수, allZeroTotalSale, consultNo });
      if (fromNewQuote && !zeroPriceTouchedRef.current) {
        // keep initial default for first entry from new quote flow
      } else if (resolved != null) {
        setZeroPriceToProduct(resolved);
      }
      
      // 2. 가격번호 추출 및 상세 정보 조회 (원가 포함)
      let costAdult = 0, costChild = 0, costInfant = 0;
      let fetchedPriceNo = null;
      
      if (rawRow?.가격번호) {
        fetchedPriceNo = Number(rawRow.가격번호);
        setPriceNo(fetchedPriceNo);
        
        try {
          const { companyNo } = getUserInfo() || {};
          const detailData = await getPriceReservationInfo({ 
            productNo: effectiveProductNo, 
            companyNo, 
            priceNo: fetchedPriceNo 
          });
          
          const detailRow = Array.isArray(detailData?.table) && detailData.table.length ? detailData.table[0] : null;
          if (detailRow?.가격정보) {
            try {
              const parsed = JSON.parse(detailRow.가격정보);
              const priceInfo = Array.isArray(parsed) && parsed.length ? parsed[0] : parsed;
              
              // 총원가 추출
              if (Array.isArray(priceInfo?.총원가) && priceInfo.총원가.length > 0) {
                const cost = priceInfo.총원가[0];
                costAdult = Number(cost.성인 || 0);
                costChild = Number(cost.어린이 || 0);
                costInfant = Number(cost.영유아 || 0);
              }
            } catch (parseErr) {
              console.warn('가격정보 파싱 실패:', parseErr);
            }
          }
        } catch (detailErr) {
          console.warn('상세 가격정보 조회 실패:', detailErr);
        }
      }
      
      // 3. 폼에 반영
      // 0표시(체크) 상태에서는 상품 일정표 판매가가 0으로 저장돼 있어, 조회된 0이
      // 폼의 실판매가를 덮어쓰지 않도록 방지한다. (실판매가가 유실되면 체크 해제 시 복원 불가)
      const keepRealSale = zeroPriceToProduct;
      setForm(prev => ({
        ...prev,
        성인판매가: (keepRealSale && !(adult > 0)) ? prev.성인판매가 : (adult ?? prev.성인판매가),
        소아판매가: (keepRealSale && !(child > 0)) ? prev.소아판매가 : (child ?? prev.소아판매가),
        유아판매가: (keepRealSale && !(infant > 0)) ? prev.유아판매가 : (infant ?? prev.유아판매가),
        성인원가: costAdult || prev.성인원가,
        소아원가: costChild || prev.소아원가,
        유아원가: costInfant || prev.유아원가,
      }));
      
      if (fetchedPriceNo) {
}
    } catch (e) {
      console.error('가격 조회 실패:', e);
      alert('가격 조회에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full relative">

      {/* 상단: 헤더 다음 영역으로 상담정보 주입, 그 아래 주문내역 */}
      <div className="flex-1 min-h-0">
        <OrderListPage
          onCreateProduct={onCreateProduct}
          creatingProduct={creatingProduct}
          hideReservationList={true}
          onOpenReservationList={() => setReservationListOpen(true)}
          headerInlineRight={(() => {
            const v = (form?.판매업체 || consultInfo?.판매업체 || consultInfo?.판매업체명 || consultInfo?.판매처 || '').toString().trim() || '직판';
            const hasLinkedProduct = effectiveProductNo != null && Number(effectiveProductNo) > 0;
            return (
              <>
                {hasLinkedProduct && (
                  <button
                    type="button"
                    className="px-2.5 py-1 text-xs rounded border border-red-300 text-red-700 hover:bg-red-50"
                    onClick={isLockedByReservation ? undefined : onDeleteLinkedProductOnly}
                    disabled={loading || isLockedByReservation}
                  >연결상품 삭제</button>
                )}
                <span className="text-sm text-gray-600 shrink-0">(판매업체: {v})</span>
              </>
            );
          })()}
          afterHeader={(
            <div className="mb-4">
              {/* Header Section */}
              <div className="bg-white rounded-2xl p-5 mb-4">
                {isLockedByReservation && (
                  <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    예약전환된 상담은 편집이 불가능 합니다.
                  </div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <h2 className="text-[17px] font-bold text-gray-900">견적 상담정보</h2>
                    {/* 상담상태 선택 UI (커스텀 드롭다운) */}
                    {(() => {
                      const meta = getConsultStatusMeta(consultStatusCode);
                      const options = [
                        { value: '', label: '상담상태 선택' },
                        { value: '420003', label: '상담요청' },
                        { value: '420001', label: '진행중' },
                        { value: '420002', label: '예약전환' },
                        { value: '420999', label: '상담취소' },
                      ];
                      const current = options.find(o => o.value === (consultStatusCode || '')) || options[0];
                      return (
                        <div className="relative" data-tour="quote-status">
                          <button
                            type="button"
                            className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium ${meta.colorClass} ${isLockedByReservation || statusSaving ? 'opacity-60 cursor-not-allowed' : ''}`}
                            onClick={() => { if (!isLockedByReservation && !statusSaving) setIsConsultStatusOpen(prev => !prev); }}
                          >
                            <span className="mx-1">
                              {current.label}
                            </span>
                            <span className="ml-1 text-[9px] text-gray-500">▼</span>
                          </button>
                          {isConsultStatusOpen && (
                            <div className="absolute z-30 mt-1 w-full min-w-[140px] bg-white rounded-md shadow-lg border border-gray-200">
                              <ul className="py-1 text-[11px] text-gray-800">
                                {options.map((opt) => (
                                  <li key={opt.value || 'empty'}>
                                    <button
                                      type="button"
                                      className={`w-full text-left px-3 py-1.5 hover:bg-gray-50 ${
                                        (consultStatusCode || '') === opt.value ? 'font-semibold text-blue-700' : ''
                                      } ${isLockedByReservation ? 'cursor-not-allowed opacity-60' : ''}`}
                                      onClick={async () => {
                                        if (isLockedByReservation || statusSaving) return;
                                        const nextCode = opt.value || null;
                                        const prevCode = String(consultStatusCode || '');
                                        if (String(nextCode || '') === prevCode) {
                                          setIsConsultStatusOpen(false);
                                          return;
                                        }
                                        // 예약전환(420002)으로 변경 시 편집 불가 안내 후 확인
                                        if (String(nextCode || '') === '420002' && prevCode !== '420002') {
                                          setIsConsultStatusOpen(false);
                                          setReservationStatusConfirmOpen(true);
                                          return;
                                        }
                                        const ok = await saveConsultStatusChange(nextCode);
                                        if (ok) setIsConsultStatusOpen(false);
                                      }}
                                    >
                                      {opt.label}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {/* 공개 견적 응찰 상태 배지 (판매사 낙찰 결과) — 참여건일 때만 노출 */}
                    {(() => {
                      const bidMeta = getBidStatusMeta(bidStatus);
                      if (!bidMeta) return null;
                      return (
                        <span
                          className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium ${bidMeta.colorClass}`}
                          title="공개 견적 응찰 상태 (판매사 낙찰 결과)"
                        >
                          {bidMeta.label}
                        </span>
                      );
                    })()}
                    {/* 공개 견적 요청 대리점 — 참여건일 때만 노출 */}
                    {bidAgencyLabel && (
                      <span
                        className="inline-flex items-center gap-1 text-[11px] text-gray-600"
                        title="이 견적을 요청한 대리점"
                      >
                        <span className="text-gray-400">요청 대리점</span>
                        <span className="font-medium text-gray-800">{bidAgencyLabel}</span>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-3.5 h-3.5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1"
                        checked={syncToProduct}
                        onChange={(e)=>!isLockedByReservation && setSyncToProduct(e.target.checked)}
                        disabled={isLockedByReservation}
                      />
                      <span data-tour="quote-sync">상품 가격/재고에도 저장</span>
                    </label>
                    <button
                      type="button"
                      data-tour="quote-preview"
                      className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                      onClick={async () => {
                        if (isLockedByReservation) return;
                        try {
                          if (consultNo) {
                            await saveConsultNote({ consultNo, recordNo: 0, content: '견적서 생성' });
                            const list = await getConsultNotes({ consultNo });
                            setNotes(Array.isArray(list) ? list : []);
                          }
                        } catch (e) {
                          try { console.error('견적서 생성 상담내역 저장 실패:', e); } catch {}
                        } finally {
                          setPreviewOpen(true);
                        }
                      }}
                    >견적서 생성</button>
                    
                    <button
                      type="button"
                      className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-red-50 text-red-600 hover:bg-red-100"
                      onClick={isLockedByReservation ? undefined : onDeleteConsult}
                      disabled={isLockedByReservation}
                    >삭제</button>
                    <button
                      type="button"
                      data-tour="quote-save"
                      className="h-9 rounded-lg px-3.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={isLockedByReservation ? undefined : onSave}
                      disabled={saving || isLockedByReservation}
                    >{saving ? '저장 중...' : '저장'}</button>
                  </div>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">불러오는 중...</span>
                  </div>
                ) : error ? (
                  <div className="bg-red-50 rounded-xl p-4">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span className="text-red-700">{error}</span>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4 items-stretch">
                    {/* 1행: 고객 정보 */}
                    <div className="bg-gray-50 rounded-xl p-4 h-full flex flex-col" data-tour="quote-customer">
                      <h3 className="text-[15px] font-bold text-gray-900 mb-3 flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                        고객 정보
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">판매거래처</label>
                          <div className="relative" ref={agencyDropdownRef}>
                            <button
                              type="button"
                              className={`w-full border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm outline-none text-left flex items-center justify-between gap-2 transition-all ${
                                isLockedByReservation
                                  ? 'bg-gray-50 text-gray-500 cursor-not-allowed'
                                  : 'bg-white hover:border-gray-400 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500'
                              }`}
                              onClick={async () => {
                                if (isLockedByReservation || loading || saving) return;
                                const next = !showAgencyPicker;
                                setShowAgencyPicker(next);
                                if (next) {
                                  setAgencySearch('');
                                  await ensureAgencyLoaded();
                                }
                              }}
                              disabled={isLockedByReservation || loading || saving}
                            >
                              <span className={`truncate ${form.판매업체 ? 'text-gray-800' : 'text-gray-500'}`}>
                                {form.판매업체
                                  ? `${form.판매업체}${form.판매업체번호 ? ` (${form.판매업체번호})` : ''}`
                                  : '직판'}
                              </span>
                              <svg
                                className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${showAgencyPicker ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {showAgencyPicker && !isLockedByReservation && (
                              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                                <div className="p-2 border-b border-gray-200">
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                                    placeholder="거래처명 / 번호 / 담당자 검색"
                                    value={agencySearch}
                                    onChange={(e) => setAgencySearch(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    autoFocus
                                  />
                                </div>
                                <div className="max-h-44 overflow-y-auto text-sm">
                                  {agencyLoading && (
                                    <div className="px-3 py-3 text-center text-gray-500">불러오는 중...</div>
                                  )}
                                  {!agencyLoading && agencyError && (
                                    <div className="px-3 py-3 text-center text-red-600">{agencyError}</div>
                                  )}
                                  {!agencyLoading && !agencyError && filteredAgencyList.length === 0 && (
                                    <div className="px-3 py-3 text-center text-gray-500">
                                      {agencySearch ? '검색 결과가 없습니다' : '표시할 거래처가 없습니다'}
                                    </div>
                                  )}
                                  {!agencyLoading && !agencyError && filteredAgencyList.length > 0 && (
                                    filteredAgencyList.slice(0, 80).map((c, idx) => {
                                      const agencyNo = String(c?.코드 ?? c?.업체번호2 ?? c?.거래처번호 ?? '').trim();
                                      const agencyName = String(c?.코드명 ?? c?.거래처명 ?? '').trim();
                                      const selected = String(form.판매업체번호 || '') === agencyNo && agencyNo !== '';
                                      return (
                                        <div
                                          key={agencyNo || idx}
                                          className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${selected ? 'bg-blue-100' : ''}`}
                                          onClick={() => selectAgency(c)}
                                        >
                                          <div className={`truncate ${selected ? 'text-blue-800' : 'text-gray-800'}`}>
                                            {agencyName || '-'}
                                          </div>
                                          <div className={`text-xs mt-0.5 truncate ${selected ? 'text-blue-700' : 'text-gray-500'}`}>
                                            {[agencyNo, c?.담당자명 || c?.대표자명].filter(Boolean).join(' · ')}
                                          </div>
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                                <div className="border-t border-gray-200">
                                  <button
                                    type="button"
                                    className="w-full px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 text-center"
                                    onClick={clearAgency}
                                  >
                                    직판 (선택 해제)
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">요청자명</label>
                          <input
                            className="w-full border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                            value={form.요청자명}
                            onChange={(e)=>!isLockedByReservation && onChange('요청자명', e.target.value)}
                            disabled={isLockedByReservation}
                            placeholder="고객명을 입력하세요"
                          />
                        </div>
                        <div>
                          <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">휴대폰</label>
                          <input
                            className="w-full border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                            value={form.요청자휴대폰}
                            onChange={(e)=>!isLockedByReservation && onChange('요청자휴대폰', e.target.value)}
                            disabled={isLockedByReservation}
                            placeholder="010-0000-0000"
                          />
                        </div>
                        <div>
                          <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">이메일</label>
                          <input
                            type="email"
                            className="w-full border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                            value={form.요청자이메일}
                            onChange={(e)=>!isLockedByReservation && onChange('요청자이메일', e.target.value)}
                            disabled={isLockedByReservation}
                            placeholder="이메일을 입력하세요"
                          />
                        </div>
                      </div>
                    </div>

                    {/* 1행: 여행 일정 & 인원 */}
                    <div className="bg-gray-50 rounded-xl p-4 h-full flex flex-col" data-tour="quote-trip">
                      <h3 className="text-[15px] font-bold text-gray-900 mb-3 flex items-center">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                        여행 일정 & 인원
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-[13px] font-semibold text-gray-600 mb-2">여행 기간</label>
                          <div className="flex items-center gap-3">
                            <div className="flex-grow">
                              <DateRangePicker
                                startDate={form.시작일}
                                endDate={form.종료일}
                                onStartDateChange={(v) => { if (!isLockedByReservation) onChange('시작일', v); }}
                                onEndDateChange={(v) => { if (!isLockedByReservation) onChange('종료일', v); }}
                                disabled={isLockedByReservation}
                                singleCalendar
                              />
                            </div>
                            {(() => {
                              if (!form.시작일 || !form.종료일) return null;
                              try {
                                const start = new Date(form.시작일);
                                const end = new Date(form.종료일);
                                if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
                                const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                                if (diff <= 0) return null;
                                return (
                                  <span className="text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1.5 rounded-md whitespace-nowrap">
                                    총 {diff}일
                                  </span>
                                );
                              } catch {
                                return null;
                              }
                            })()}
                          </div>
                        </div>
                        <div>
                          <label className="block text-[13px] font-semibold text-gray-600 mb-2">인원 구성</label>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-[13px] font-semibold text-gray-600 mb-1">성인</label>
                              <input
                                type="number"
                                min="0"
                                className="w-full border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm outline-none text-center focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                                value={form.성인수}
                                onChange={(e)=>!isLockedByReservation && onChange('성인수', Number(e.target.value))}
                                disabled={isLockedByReservation}
                              />
                            </div>
                            <div>
                              <label className="block text-[13px] font-semibold text-gray-600 mb-1">소아</label>
                              <input
                                type="number"
                                min="0"
                                className="w-full border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm outline-none text-center focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                                value={form.소아수}
                                onChange={(e)=>!isLockedByReservation && onChange('소아수', Number(e.target.value))}
                                disabled={isLockedByReservation}
                              />
                            </div>
                            <div>
                              <label className="block text-[13px] font-semibold text-gray-600 mb-1">유아</label>
                              <input
                                type="number"
                                min="0"
                                className="w-full border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm outline-none text-center focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                                value={form.유아수}
                                onChange={(e)=>!isLockedByReservation && onChange('유아수', Number(e.target.value))}
                                disabled={isLockedByReservation}
                              />
                            </div>
                          </div>
                          <div className="rounded-md bg-gray-50 flex items-center justify-between p-3 mt-3">
                            <span className="text-sm font-medium text-gray-700">총 인원</span>
                            <span className="text-lg font-bold text-blue-600">{(form.성인수 || 0) + (form.소아수 || 0) + (form.유아수 || 0)}명</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 1행: 상담내역 */}
                    <div className="bg-gray-50 rounded-xl p-4 h-full flex flex-col" data-tour="quote-notes">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-[15px] font-bold text-gray-900 flex items-center">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                          상담내역
                        </h3>
                        <div className="flex items-center gap-2">
                          <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {notesLoading ? '불러오는 중...' : `${Array.isArray(notes) ? notes.length : 0}건`}
                          </div>
                        </div>
                      </div>

                      {!consultNo && (
                        <div className="text-xs text-gray-500 bg-yellow-50 border border-yellow-200 rounded-md p-2 mb-3">
                          상담번호가 있어야 상담내역을 추가/편집할 수 있습니다.
                        </div>
                      )}

                      {notesError && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700 mb-3">{notesError}</div>
                      )}

                      {(Array.isArray(notes) ? notes.length : 0) > 0 && (
                        <div className={`rounded-md overflow-hidden ${(Array.isArray(notes) ? notes.length : 0) >= 4 ? 'max-h-[240px] overflow-y-auto' : ''}`}>
                          {(Array.isArray(notes) ? notes : []).map((row) => {
                            const key = row?.상담기록번호 ?? Math.random();
                            const ts = row?.등록일시 || row?.등록일 || '';
                            let when = ts;
                            try {
                              const iso = String(ts).replace(' ', 'T');
                              const d = new Date(iso);
                              if (!isNaN(d.getTime())) when = d.toLocaleString();
                            } catch {}
                            const author = row?.등록자 || '-';
                            const content = row?.내용 || '';
                            return (
                              <div key={key} className="px-3 py-2 border-b border-gray-100 last:border-b-0 bg-white hover:bg-gray-50 transition-colors">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                                      <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">{author}</span>
                                      <span className="text-gray-400">•</span>
                                      <span>{when}</span>
                                    </div>
                                    <div className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">{content}</div>
                                  </div>
                                  <div className="flex-shrink-0 flex gap-1">
                                    <button 
                                      onClick={()=>{ if (!isLockedByReservation) onDeleteNote(row); }} 
                                      disabled={notesLoading || isLockedByReservation} 
                                      className="px-2 py-1 text-xs rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50 transition-all">
                                      ✕
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="flex justify-center pt-3">
                        <button
                          type="button"
                          onClick={() => { if (isLockedByReservation) return; setEditingId(null); setNoteContent(''); setNoteModalOpen(true); }}
                          disabled={!consultNo || isLockedByReservation}
                          title={consultNo ? '상담내역 추가' : '상담번호가 있어야 상담내역을 추가할 수 있습니다'}
                          className="w-30 px-3 py-2 text-xs rounded-md border border-dashed border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                        >
                          <span className="text-base leading-none">+</span>
                          <span>상담내역 추가</span>
                        </button>
                      </div>
                    </div>

                    {/* 2행: 견적 내용 */}
                    <div className="bg-gray-50 rounded-xl p-4 h-full flex flex-col">
                      <h3 className="text-[15px] font-bold text-gray-900 mb-3 flex items-center">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                        견적 내용
                      </h3>
                      <div className="space-y-3 flex-1 min-h-0">
                        <div>
                          <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">견적명</label>
                          <input
                            className="w-full border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                            value={form.견적명}
                            onChange={(e)=>!isLockedByReservation && onChange('견적명', e.target.value)}
                            disabled={isLockedByReservation}
                            placeholder="견적서명을 입력하세요"
                          />
                        </div>
                        {form.여행지도시 ? (
                          <div>
                            <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">여행지 도시</label>
                            <input
                              className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-700"
                              value={form.여행지도시}
                              readOnly
                            />
                          </div>
                        ) : null}
                        <div className="flex-1 min-h-0 flex flex-col">
                          <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">견적내용</label>
                          <textarea
                            rows={12}
                            className="w-full flex-1 min-h-0 border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none transition-all"
                            value={form.견적내용}
                            onChange={(e)=>!isLockedByReservation && onChange('견적내용', e.target.value)}
                            disabled={isLockedByReservation}
                            placeholder="고객 요청사항을 입력하세요"
                          />
                        </div>
                      </div>
                    </div>

                    {/* 2행: 원가 & 판매가 (1인 기준) */}
                    <div className="bg-gray-50 rounded-xl p-4 h-full flex flex-col" data-tour="quote-price">
                      <h3 className="text-[15px] font-bold text-gray-900 mb-3 flex items-center">
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                        원가 & 판매가 (1인 기준)
                      </h3>
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          
                        </div>
                        {/* 원가 합계 */}
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={openCostItemModal}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') openCostItemModal();
                          }}
                          className="grid gap-2 cursor-pointer"
                          style={{ gridTemplateColumns: `repeat(${1 + (form.소아수 > 0 ? 1 : 0) + (form.유아수 > 0 ? 1 : 0)}, minmax(0, 1fr))` }}
                        >
                          <div>
                            <label className="block text-[13px] font-semibold text-gray-600 mb-1">성인 원가</label>
                            <div className="px-3 py-2 text-sm text-right rounded-md border border-gray-300 font-semibold text-red-700 hover:bg-primary-50">
                              {fmt(aggregatedPerPersonCost.성인 || form.성인원가)}원
                            </div>
                          </div>
                          {form.소아수 > 0 && (
                            <div>
                              <label className="block text-[13px] font-semibold text-gray-600 mb-1">소아 원가</label>
                              <div className="px-3 py-2 text-sm text-right  rounded-md border border-gray-300 font-semibold text-red-700">
                                {fmt(aggregatedPerPersonCost.어린이 || form.소아원가)}원
                              </div>
                            </div>
                          )}
                          {form.유아수 > 0 && (
                            <div>
                              <label className="block text-[13px] font-semibold text-gray-600 mb-1">유아 원가</label>
                              <div className="px-3 py-2 text-sm text-right  rounded-md border border-gray-300 font-semibold text-red-700">
                                {fmt(aggregatedPerPersonCost.영유아 || form.유아원가)}원
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* 판매가 입력 */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-xs text-gray-600">판매가 설정</label>
                          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                            <input
                              type="checkbox"
                              className="w-3.5 h-3.5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1"
                              checked={zeroPriceToProduct}
                              onChange={(e)=>{
                                if (isLockedByReservation) return;
                                zeroPriceTouchedRef.current = true;
                                setZeroPriceToProduct(e.target.checked);
                                writeZeroFlagLocal(consultNo, e.target.checked);
                              }}
                              disabled={isLockedByReservation}
                            />
                            <span>일정표에 0원 표시</span>
                          </label>
                        </div>
                        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${1 + (form.소아수 > 0 ? 1 : 0) + (form.유아수 > 0 ? 1 : 0)}, minmax(0, 1fr))` }}>
                          <div>
                            <label className="block text-[13px] font-semibold text-gray-600 mb-1">성인 판매가</label>
                            <input
                              type="text"
                              inputMode="numeric"
                              className="w-full border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm outline-none text-right focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                              value={fmt(form.성인판매가)}
                              onChange={(e)=>!isLockedByReservation && onChange('성인판매가', parseNumberInput(e.target.value))}
                              disabled={isLockedByReservation}
                              placeholder="판매가"
                            />
                          </div>
                          {form.소아수 > 0 && (
                            <div>
                              <label className="block text-[13px] font-semibold text-gray-600 mb-1">소아 판매가</label>
                              <input
                                type="text"
                                inputMode="numeric"
                                className="w-full border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm outline-none text-right focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                                value={fmt(form.소아판매가)}
                                onChange={(e)=>!isLockedByReservation && onChange('소아판매가', parseNumberInput(e.target.value))}
                                disabled={isLockedByReservation}
                                placeholder="판매가"
                              />
                            </div>
                          )}
                          {form.유아수 > 0 && (
                            <div>
                              <label className="block text-[13px] font-semibold text-gray-600 mb-1">유아 판매가</label>
                              <input
                                type="text"
                                inputMode="numeric"
                                className="w-full border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm outline-none text-right focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                                value={fmt(form.유아판매가)}
                                onChange={(e)=>!isLockedByReservation && onChange('유아판매가', parseNumberInput(e.target.value))}
                                disabled={isLockedByReservation}
                                placeholder="판매가"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* 합계 정보 */}
                      <div className="pt-3 border-gray-200 space-y-2 bg-gray-50 rounded-md p-3">
                        <div className="flex items_center justify-between text-sm">
                          <span className="text-gray-600">총 원가</span>
                          <span className="font-bold text-red-600">{fmt(totalCostAmount)}원</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">총 판매가</span>
                          <span className="font-bold text-blue-600">{fmt(totalSaleAmount)}원</span>
                        </div>
                        <div className="flex items-center justify-between text-lg border-t border-gray-300 pt-2">
                          <span className="font-semibold text-gray-700">예상 수익</span>
                          <span className={`font-bold ${totalProfitAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}> 
                            {fmt(totalProfitAmount)}원
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* 2행: 첨부파일 */}
                    <div className="bg-gray-50 rounded-xl p-4 h-full flex flex-col">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-[15px] font-bold text-gray-900 flex items-center">
                          <div className="w-2 h-2 bg-gray-500 rounded-full mr-2"></div>
                          첨부파일
                        </h3>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{Array.isArray(form.견적첨부파일리스트) ? form.견적첨부파일리스트.length : 0}개</span>
                      </div>

                      {/* 자동생성 견적서 */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs text-gray-600 font-medium">자동 생성 견적서</div>
                        </div>
                        <div className="overflow-hidden max-h-40 lg:max-h-[40vh] overflow-y-auto">
                        {(Array.isArray(form.견적첨부파일리스트) ? form.견적첨부파일리스트.filter(f=>f && isAutoGeneratedQuoteAttachment(f)) : []).map((f, idx) => {
                          const url = resolveAttachmentUrl(f);
                          const fileName = f?.파일명 || (f?.첨부파일경로 ? String(f.첨부파일경로).split('/').pop() : '') || '파일';
                          const isHtml = isHtmlAttachmentName(fileName);
                          const originalIndex = Array.isArray(form.견적첨부파일리스트)
                            ? form.견적첨부파일리스트.findIndex(x => x === f || (x?.첨부파일경로 && x.첨부파일경로 === f?.첨부파일경로) || (x?.url && x.url === f?.url))
                            : idx;
                          return (
                            <div key={`${f.첨부파일경로 || f.url || fileName}-${idx}`} className="p-3 flex items-center justify-between bg-white hover:bg-gray-50">
                              <div className="min-w-0 flex-1 w-0">
                                {url ? (
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    download={isHtml ? undefined : fileName}
                                    className="block text-xs text-blue-700 hover:underline truncate"
                                    title={isHtml ? '열기' : '다운로드'}
                                  >
                                    {fileName}
                                  </a>
                                ) : (
                                  <div className="block text-xs text-gray-900 truncate">{fileName}</div>
                                )}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                             
                                <button onClick={()=>{ if (!isLockedByReservation) onRemoveAttachment(originalIndex); }} disabled={isLockedByReservation} className="px-2 py-1 text-xs rounded text-red-600 hover:bg-red-50 disabled:opacity-50">X</button>
                              </div>
                            </div>
                          );
                        })}
                        {Array.isArray(form.견적첨부파일리스트) && form.견적첨부파일리스트.filter(f=>f && isAutoGeneratedQuoteAttachment(f)).length === 0 && (
                          <div className="p-3 text-left text-xs text-gray-500 bg-white">없음</div>
                        )}
                        </div>
                      </div>

                      {/* 수동 첨부 */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs text-gray-600 font-medium">수동 첨부파일</div>
                        </div>
                        <input
                          ref={manualAttachInputRef}
                          type="file"
                          onChange={isLockedByReservation ? undefined : onSelectFile}
                          disabled={isLockedByReservation}
                          className="hidden"
                        />
                        <div className="rounded-md overflow-hidden max-h-40 overflow-y-auto">
                        {(Array.isArray(form.견적첨부파일리스트) ? form.견적첨부파일리스트.filter(f=>!isAutoGeneratedQuoteAttachment(f)) : []).map((f, idx) => {
                          const url = resolveAttachmentUrl(f);
                          const fileName = f?.파일명 || (f?.첨부파일경로 ? String(f.첨부파일경로).split('/').pop() : '') || '파일';
                          const isHtml = isHtmlAttachmentName(fileName);
                          const originalIndex = Array.isArray(form.견적첨부파일리스트)
                            ? form.견적첨부파일리스트.findIndex(x => x === f || (x?.첨부파일경로 && x.첨부파일경로 === f?.첨부파일경로) || (x?.url && x.url === f?.url))
                            : idx;
                          return (
                            <div key={`${f.첨부파일경로 || f.url || fileName}-manual-${idx}`} className="p-3 border-b border-gray-100 last:border-b-0 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors">
                              <div className="min-w-0 flex-1 w-0">
                                {url ? (
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    download={isHtml ? undefined : fileName}
                                    className="block text-xs text-blue-700 hover:underline truncate"
                                    title={isHtml ? '열기' : '다운로드'}
                                  >
                                    {fileName}
                                  </a>
                                ) : (
                                  <div className="block text-sm text-gray-900 truncate">{fileName}</div>
                                )}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {url && (
                                  <>
                                    
                                  </>
                                )}
                                <button onClick={()=>onRemoveAttachment(originalIndex)} className="px-2 py-1 text-xs rounded-md text-red-600 hover:bg-red-50 transition-all">✕</button>
                              </div>
                            </div>
                          );
                        })}
                        </div>

                        <div className="mt-2 flex justify-center">
                          <button
                            type="button"
                            onClick={() => { if (!isLockedByReservation) manualAttachInputRef.current?.click(); }}
                            disabled={uploading || isLockedByReservation}
                            className="w-30 px-3 py-2 text-xs rounded-md border border-dashed border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                          >
                            <span className="text-base leading-none">+</span>
                            <span>{uploading ? '업로드 중...' : '파일 첨부'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        />
        
        {/* 상담내역 편집 모달 */}
        <ConsultNoteEditModal
          open={noteModalOpen}
          onClose={onCancelEdit}
          initialContent={noteContent}
          title={editingId ? '상담내역 편집' : '상담내역 추가'}
          saving={noteSaving}
          onSave={(text) => onSaveNote(text)}
        />
        <TourReservationModal
          open={reserveOpen}
          onClose={() => setReserveOpen(false)}
          priceNo={priceNo}
          date={form.시작일}
          productName={navProductName || ''}
        />
        <QuotePreviewModal
          open={previewOpen}
          onClose={async () => {
            setPreviewOpen(false);
            // 견적서 미리보기 모달 닫을 때 상담 정보 저장
            if (consultNo) {
              try {
                await saveConsultInfo({
                  consultNo,
                  productNo: effectiveProductNo,
                  form,
                  consultStatusCode,
                  consultTypeCode: '419002',
                  zeroPriceToProduct,
                });
                console.log('미리보기 모달 닫을 때 상담 정보 저장 성공');
              } catch (saveErr) {
                console.error('미리보기 모달 닫을 때 상담 정보 저장 실패:', saveErr);
              }
            }
          }}
          data={form}
          productNo={effectiveProductNo}
          productName={navProductName}
          onAttachmentAdded={(item) => {
            setForm(prev => ({
              ...prev,
              견적첨부파일리스트: Array.isArray(prev.견적첨부파일리스트)
                ? [...prev.견적첨부파일리스트, { ...item, 자동생성: true }]
                : [item]
            }));
          }}
        />
        <TourReservationModal
          open={reserveOpen}
          onClose={() => setReserveOpen(false)}
          priceNo={priceNo}
          date={form.시작일}
          productName={navProductName || ''}
          productNo={effectiveProductNo}
        />
        <QuoteEmailModal
          open={emailOpen}
          onClose={async () => {
            setEmailOpen(false);
            // 자동 생성 견적서 모달 닫을 때 상담 정보 저장
            if (consultNo && emailPdfUrl) {
              try {
                await saveConsultInfo({
                  consultNo,
                  productNo: effectiveProductNo,
                  form,
                  consultStatusCode,
                  consultTypeCode: '419002',
                  zeroPriceToProduct,
                });
                console.log('이메일 모달 닫을 때 상담 정보 저장 성공');
              } catch (saveErr) {
                console.error('이메일 모달 닫을 때 상담 정보 저장 실패:', saveErr);
              }
            }
          }}
          recipientEmail={form?.요청자이메일 || ''}
          quoteName={form?.견적명 || navProductName}
          period={form?.시작일 && form?.종료일 ? `${form.시작일} ~ ${form.종료일}` : (form?.시작일 || form?.종료일 || '')}
          pdfUrl={emailPdfUrl}
          attachments={emailAttachmentList}
        />

        {cityPickOpen && (
          <div
            className="fixed inset-0 z-[80] flex items-center justify-center p-4"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeCityPicker(null);
            }}
          >
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative bg-white w-full max-w-xl max-h-[85vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-6 pt-5 pb-3">
                <div className="min-w-0">
                  <div className="text-[17px] font-bold text-gray-900">도시 선택</div>
                  <div className="mt-0.5 text-[14px] text-blue-600 font-bold">
                    견적(상담)과 연결된 상품을 생성하기 위해 상품의 대표 도시를 선택해 주세요.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => closeCityPicker(null)}
                  className="size-8 rounded-lg text-gray-400 hover:bg-gray-100"
                >
                  ✕
                </button>
              </div>

              <div className="p-4 border-b border-gray-200">
                <input
                  autoFocus
                  value={cityPickQuery}
                  onChange={(e) => setCityPickQuery(e.target.value)}
                  placeholder="도시 검색 (초성/이름)"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
                {cityPickLoading && (
                  <div className="mt-2 text-xs text-gray-500">불러오는 중...</div>
                )}
                {cityPickError && (
                  <div className="mt-2 text-xs text-red-600">{cityPickError}</div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto">
                {(!cityPickLoading && !cityPickError && Array.isArray(filteredCityPickOptions) && filteredCityPickOptions.length === 0) && (
                  <div className="p-4 text-sm text-gray-500">검색 결과가 없습니다.</div>
                )}
                {(Array.isArray(filteredCityPickOptions) ? filteredCityPickOptions : []).map((opt) => {
                  const code = String(opt?.코드 || opt?.도시번호 || '');
                  const name = String(opt?.코드명 || opt?.도시명 || '');
                  const selected = code && String(cityPickSelectedCode) === code;
                  return (
                    <button
                      key={code || name}
                      type="button"
                      className={`w-full text-left px-4 py-2 text-sm border-b border-gray-100 hover:bg-gray-50 ${selected ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-900'}`}
                      onClick={() => {
                        setCityPickSelectedCode(code);
                      }}
                    >
                      {name}
                      {code ? <span className="ml-2 text-xs text-gray-400">({code})</span> : null}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-end gap-2 px-6 pb-5 pt-3">
                <button
                  type="button"
                  onClick={() => closeCityPicker(null)}
                  className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200"
                >
                  취소
                </button>
                <button
                  type="button"
                  disabled={cityPickLoading || !cityPickSelectedCode}
                  onClick={() => {
                    const selected = (Array.isArray(cityPickOptions) ? cityPickOptions : []).find((c) => {
                      const code = String(c?.코드 || c?.도시번호 || '');
                      return code && code === String(cityPickSelectedCode);
                    });
                    const cityNo = String(selected?.코드 || selected?.도시번호 || cityPickSelectedCode || '');
                    const cityName = String(selected?.코드명 || selected?.도시명 || '');
                    if (!cityNo) return;
                    closeCityPicker({ cityNo, cityName });
                  }}
                  className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  선택
                </button>
              </div>
            </div>
          </div>
        )}

        {costItemModalOpen && (
          <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={cancelCostDraft} />
            <div className="relative bg-white w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-6 pt-5 pb-3">
                <div className="text-[17px] font-bold text-gray-900">원가 항목 설정</div>
                <button onClick={cancelCostDraft} className="size-8 rounded-lg text-gray-400 hover:bg-gray-100">✕</button>
              </div>

              <div className="p-4 flex-1 overflow-y-auto">
                <div className="mb-3">
                  <div
                    className="grid gap-2 items-center"
                    style={{ gridTemplateColumns: `minmax(0, 2fr) repeat(${1 + (form.소아수 > 0 ? 1 : 0) + (form.유아수 > 0 ? 1 : 0)}, minmax(0, 1fr)) auto` }}
                  >
                    <div className="text-[11px] font-semibold text-gray-600">항목명</div>
                    <div className="text-[11px] font-semibold text-gray-600 text-right">성인</div>
                    {form.소아수 > 0 && <div className="text-[11px] font-semibold text-gray-600 text-right">소아</div>}
                    {form.유아수 > 0 && <div className="text-[11px] font-semibold text-gray-600 text-right">유아</div>}
                    <div className="text-[11px] font-semibold text-gray-600 text-right">삭제</div>
                  </div>
                </div>

                {(Array.isArray(costDraftList) ? costDraftList : []).length === 0 ? (
                  <div className="text-sm text-gray-500 bg-gray-50 rounded-xl p-3">설정된 항목이 없습니다.</div>
                ) : (
                  <div className="space-y-2">
                    {(Array.isArray(costDraftList) ? costDraftList : []).map((row, idx) => (
                      <div
                        key={idx}
                        className="grid gap-2 items-center"
                        style={{ gridTemplateColumns: `minmax(0, 2fr) repeat(${1 + (form.소아수 > 0 ? 1 : 0) + (form.유아수 > 0 ? 1 : 0)}, minmax(0, 1fr)) auto` }}
                      >
                        <input
                          className="border border-gray-300 rounded px-2 py-1.5 text-xs"
                          value={row?.원가항목 || ''}
                          onChange={(e)=>!isLockedByReservation && updateCostDraftItem(idx, '원가항목', e.target.value)}
                          disabled={isLockedByReservation}
                          placeholder="항목명"
                        />
                        <input
                          type="text"
                          inputMode="numeric"
                          className="border border-gray-300 rounded px-2 py-1.5 text-xs text-right"
                          value={fmt(row?.성인)}
                          onChange={(e)=>!isLockedByReservation && updateCostDraftItem(idx, '성인', e.target.value)}
                          disabled={isLockedByReservation}
                          placeholder="성인"
                        />
                        {form.소아수 > 0 && (
                          <input
                            type="text"
                            inputMode="numeric"
                            className="border border-gray-300 rounded px-2 py-1.5 text-xs text-right"
                            value={fmt(row?.어린이)}
                            onChange={(e)=>!isLockedByReservation && updateCostDraftItem(idx, '어린이', e.target.value)}
                            disabled={isLockedByReservation}
                            placeholder="소아"
                          />
                        )}
                        {form.유아수 > 0 && (
                          <input
                            type="text"
                            inputMode="numeric"
                            className="border border-gray-300 rounded px-2 py-1.5 text-xs text-right"
                            value={fmt(row?.영유아)}
                            onChange={(e)=>!isLockedByReservation && updateCostDraftItem(idx, '영유아', e.target.value)}
                            disabled={isLockedByReservation}
                            placeholder="유아"
                          />
                        )}
                        <button onClick={()=>removeCostDraftItem(idx)} className="px-2 py-1.5 text-xs rounded border border-red-300 text-red-600 hover:bg-red-50 transition-all">×</button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={addCostDraftItem}
                    className="w-30 px-3 py-2 text-xs rounded-md border border-dashed border-gray-300 text-gray-700 hover:bg-gray-50 inline-flex items-center justify-center gap-2"
                  >
                    <span className="text-base leading-none">+</span>
                    <span>항목 추가</span>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 px-6 pb-5 pt-3">
                
                <button onClick={saveCostDraft} className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700">저장</button>
              </div>
            </div>
          </div>
        )}
        
        {reservationStatusConfirmOpen && (
          <div
            className="fixed inset-0 z-[85] flex items-center justify-center p-4"
            onMouseDown={(e) => {
              if (!statusSaving && e.target === e.currentTarget) setReservationStatusConfirmOpen(false);
            }}
          >
            <div className="absolute inset-0 bg-black/40" />
            <div
              className="relative bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
              role="dialog"
              aria-modal="true"
              aria-labelledby="reservation-status-confirm-title"
            >
              <div className="px-5 pt-5 pb-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-base font-bold">
                    !
                  </div>
                  <div className="min-w-0">
                    <h3 id="reservation-status-confirm-title" className="text-[17px] font-bold text-gray-900">
                      예약전환 확인
                    </h3>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                      상담상태를 예약전환으로 변경하면 이 상담의 정보는 이후 편집 및 수정이 불가능합니다. 계속 진행하시겠습니까?
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 px-6 pb-5 pt-3">
                <button
                  type="button"
                  onClick={() => setReservationStatusConfirmOpen(false)}
                  disabled={statusSaving}
                  className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  취소
                </button>
                <button
                  type="button"
                  disabled={statusSaving}
                  onClick={async () => {
                    const ok = await saveConsultStatusChange('420002');
                    if (ok) setReservationStatusConfirmOpen(false);
                  }}
                  className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {statusSaving ? '저장 중...' : '예약전환'}
                </button>
              </div>
            </div>
          </div>
        )}

        {reservationListOpen && (
          <div
            className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) setReservationListOpen(false);
            }}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-[95vw] max-w-6xl h-[85vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-6 pt-5 pb-3">
                <div className="text-[17px] font-bold text-gray-900">예약 리스트</div>
                <button
                  type="button"
                  className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200"
                  onClick={() => setReservationListOpen(false)}
                >
                  닫기
                </button>
              </div>
              <div className="flex-1 overflow-auto p-4">
                <OrderListPage hideHeader={true} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
